import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { VTTToolbar } from "@/components/VTTToolbar";
import { TokenBrowserPanel } from "@/components/vtt/TokenBrowserPanel";
import { AssetBrowserPanel } from "@/components/vtt/AssetBrowserPanel";
import { SpellBrowserPanel } from "@/components/vtt/SpellBrowserPanel";
import { VTTAssetSizePanel } from "@/components/vtt/VTTAssetSizePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, Grid3X3, ZoomIn, ZoomOut, Users, Skull, UserCircle2,
  ChevronLeft, ChevronRight, Search, X, Heart, Shield, MapPin,
  Maximize2, RotateCcw, Upload, Wand2, Swords, Package, Map, Dices, FileText, Lightbulb, Eye
} from "lucide-react";
import { useVTTSocket } from "@/hooks/useVTTSocket";
import { SPELL_EFFECTS, SPELL_SCHOOLS, getSpellSVG } from "@/data/spellEffects";
import { ASSET_CATEGORIES, findAsset } from "@/data/mapAssets";
import { DND_CONDITIONS, getConditionColor } from "@/components/vtt/constants";
import { VTTContextMenu } from "@/components/vtt/VTTContextMenu";
import { VTTCombatTracker } from "@/components/vtt/VTTCombatTracker";
import { VTTTokenStatPanel } from "@/components/vtt/VTTTokenStatPanel";
import { VTTCampaignPanel } from "@/components/vtt/VTTCampaignPanel";
import { computeVisibilityPolygon, polygonToPath, tokenVisionSources, assetLightSources, segmentBlockedByWall } from "@/lib/lightingEngine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const SIZE_MAP = { "Tiny": 0.5, "Small": 1, "Medium": 1, "Large": 2, "Huge": 3, "Gargantuan": 4 };

// Resolve entity image URL - handles CDN, relative paths, and "generating" state
const resolveImageUrl = (url) => {
  if (!url || url === "generating") return null;
  if (url.startsWith("http")) return url;
  return `${FILE_URL}/${url}`;
};

// DND_CONDITIONS and getConditionColor imported from components/vtt/constants.js

// SVG measurement shape renderer
const MeasureShape = ({ shape, gs, preview }) => {
  const op = preview ? 0.4 : 0.7;
  const { startX: sx, startY: sy, endX: ex, endY: ey, type } = shape;
  if (type === "measure_line") {
    const dist = Math.sqrt((ex - sx) ** 2 + (ey - sy) ** 2);
    const ft = Math.round(dist / gs * 5);
    return (<g><line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#00ff88" strokeWidth="3" opacity={op} /><text x={(sx + ex) / 2 + 8} y={(sy + ey) / 2 - 8} fill="#00ff88" fontSize="14" fontWeight="bold">{ft}ft</text></g>);
  }
  if (type === "measure_cone") {
    const dx = ex - sx, dy = ey - sy, angle = Math.atan2(dy, dx), len = Math.sqrt(dx * dx + dy * dy), spread = Math.PI / 6;
    const p1x = sx + len * Math.cos(angle - spread), p1y = sy + len * Math.sin(angle - spread);
    const p2x = sx + len * Math.cos(angle + spread), p2y = sy + len * Math.sin(angle + spread);
    const path = `M ${sx} ${sy} L ${p1x} ${p1y} A ${len} ${len} 0 0 1 ${p2x} ${p2y} Z`;
    const ft = Math.round(len / gs * 5);
    return <g><path d={path} fill="rgba(255,102,0,0.15)" stroke="#ff6600" strokeWidth="2" opacity={op} /><text x={sx + dx * 0.4 + 8} y={sy + dy * 0.4 - 8} fill="#ff6600" fontSize="14" fontWeight="bold">{ft}ft cone</text></g>;
  }
  if (type === "measure_circle") {
    const r = Math.sqrt((ex - sx) ** 2 + (ey - sy) ** 2);
    const ft = Math.round(r / gs * 5);
    return (<g><circle cx={sx} cy={sy} r={r} fill="rgba(100,100,255,0.12)" stroke="#6666ff" strokeWidth="2" opacity={op} /><text x={sx + 5} y={sy - r - 5} fill="#6666ff" fontSize="14" fontWeight="bold">{ft}ft radius</text></g>);
  }
  if (type === "measure_square") {
    const w = ex - sx, h = ey - sy;
    const ft = Math.round(Math.max(Math.abs(w), Math.abs(h)) / gs * 5);
    return <g><rect x={Math.min(sx, ex)} y={Math.min(sy, ey)} width={Math.abs(w)} height={Math.abs(h)} fill="rgba(255,68,255,0.12)" stroke="#ff44ff" strokeWidth="2" opacity={op} /><text x={Math.min(sx, ex) + 5} y={Math.min(sy, ey) - 5} fill="#ff44ff" fontSize="14" fontWeight="bold">{ft}ft</text></g>;
  }
  return null;
};

// Find the wall segment nearest to a click point (within `tolerance` world px).
// Returns the wall object, or null if nothing's close enough.
function pickWallNear(px, py, walls, tolerance) {
  let best = null;
  let bestDist = tolerance;
  for (const w of (walls || [])) {
    const vx = w.x2 - w.x1;
    const vy = w.y2 - w.y1;
    const len2 = vx * vx + vy * vy || 1;
    let t = ((px - w.x1) * vx + (py - w.y1) * vy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = w.x1 + t * vx;
    const cy = w.y1 + t * vy;
    const d = Math.hypot(px - cx, py - cy);
    if (d < bestDist) {
      bestDist = d;
      best = w;
    }
  }
  return best;
}

const VTTPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [entities, setEntities] = useState({ npcs: [], characters: [], creatures: [], encounters: [] });
  const [maps, setMaps] = useState([]);
  const [allNpcs, setAllNpcs] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [allHandouts, setAllHandouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [spellBrowserOpen, setSpellBrowserOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState("New Session");
  const [selectedMapId, setSelectedMapId] = useState("");
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [hpInput, setHpInput] = useState("");
  const [activeTool, setActiveTool] = useState("select");
  const [activeLayer, setActiveLayer] = useState("tokens");
  const [measurePersistent, setMeasurePersistent] = useState(false);
  const [isDraggingMap, setIsDraggingMap] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [infoPanel, setInfoPanel] = useState(null);
  const [charEquipment, setCharEquipment] = useState([]);
  const [tokenInfoOpen, setTokenInfoOpen] = useState(null); // token data for stat block panel
  const [shapes, setShapes] = useState([]); // measurement shapes on map
  const [pings, setPings] = useState([]); // ping markers
  const [drawStart, setDrawStart] = useState(null); // for drawing measurement shapes
  const [drawPreview, setDrawPreview] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null); // {x,y,w,h} for drag-select
  const [fogRectStart, setFogRectStart] = useState(null); // {x,y} fog rectangle drag
  const [fogRectPreview, setFogRectPreview] = useState(null); // {x,y,w,h}
  // Fog opacity is fixed: DM sees 0.4 (so they can see what's underneath), players see 0.9
  const dmFogOpacity = 0.4;
  const [selectedTokenIds, setSelectedTokenIds] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]); // multi-select for rubber-band
  const [combatLog, setCombatLog] = useState([]); // ring buffer of last 80 combat events {id,ts,kind,text}
  const [tokenBrowserOpen, setTokenBrowserOpen] = useState(false);
  const [assetBrowserOpen, setAssetBrowserOpen] = useState(false);

  // Phase 3 — Dynamic Lighting state
  const [walls, setWalls] = useState([]);                 // [{id, x1,y1,x2,y2, kind:"wall"|"door", open:bool}]
  const [wallStart, setWallStart] = useState(null);       // First click point while drawing
  const [wallPreview, setWallPreview] = useState(null);   // Live preview point under cursor
  const [lightingMode, setLightingMode] = useState("persistent"); // "persistent" | "los"
  const [lightingEnabled, setLightingEnabled] = useState(false);  // Master toggle; off until DM places walls
  const [revealedPolygons, setRevealedPolygons] = useState([]);   // Accumulated reveal polygons (persistent mode)
  const wallsSaveTimerRef = useRef(null);
  const revealedSaveTimerRef = useRef(null);

  const pushCombatLog = (kind, text) => {
    const entry = { id: Date.now() + Math.random(), ts: new Date().toISOString(), kind, text };
    setCombatLog(prev => {
      const next = [...prev, entry];
      return next.length > 80 ? next.slice(next.length - 80) : next;
    });
  };
  const clearCombatLog = () => setCombatLog([]);
  const [diceRolls, setDiceRolls] = useState([]); // {id, roller, formula, result, timestamp}
  const [resizeToken, setResizeToken] = useState(null); // {id, startX, startY, origSize}
  const [initiativeOrder, setInitiativeOrder] = useState([]); // [{id, name, initiative, isActive}]
  const [showInitTracker, setShowInitTracker] = useState(false);
  const [fullStatBlock, setFullStatBlock] = useState(null); // Full entity data for stat panel
  const [conditionPickerOpen, setConditionPickerOpen] = useState(false);
  const [fogBrushSize, setFogBrushSize] = useState(40); // brush radius in world pixels
  const [isFogPainting, setIsFogPainting] = useState(false); // currently painting fog
  const fogSaveTimerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null); // {x, y, token} for right-click
  const [mapStates, setMapStates] = useState({}); // {mapUrl: {tokens, fog_areas}}
  const [sharedHandout, setSharedHandout] = useState(null); // popup handout for DM to share
  const [combatActive, setCombatActive] = useState(false); // combat mode from encounter launch
  const [spellEffects, setSpellEffects] = useState([]); // placed spell effects [{id, spell, x, y, rotation}]
  const spellsSaveTimerRef = useRef(null);
  const [castingSpell, setCastingSpell] = useState(null); // spell being placed
  const [spellRotation, setSpellRotation] = useState(0); // rotation for cones/lines
  const [selectedSpellId, setSelectedSpellId] = useState(null); // selected placed spell (for move/delete)
  const spellDragRef = useRef({ active: false, id: null, offsetX: 0, offsetY: 0 });
  // Drag-preview state — shown while dragging a spell from the sidebar onto the canvas
  const [spellDragPreview, setSpellDragPreview] = useState(null); // { spell, x, y } in map coords
  // Map asset library state
  const [mapAssets, setMapAssetsState] = useState([]); // placed assets
  const [placingAsset, setPlacingAsset] = useState(null); // asset key being placed
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const assetDragRef = useRef({ active: false, id: null, offsetX: 0, offsetY: 0 });
  const assetsSaveTimerRef = useRef(null);
  const [tokenAnim, setTokenAnim] = useState({}); // { [tokenId]: "attack"|"damage"|"heal"|"death" }
  const tokenAnimTimersRef = useRef({}); // map of tokenId -> timeout
  const prevTokensRef = useRef({}); // { [tokenId]: { hp, is_dead } } — to detect changes

  // Viewport state (DOM-based pan/zoom)
  const [viewport, setViewport] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1, startX: 0, startY: 0, startVx: 0, startVy: 0 });
  const [dragToken, setDragToken] = useState(null);
  const canvasAreaRef = useRef(null);

  // Socket.IO
  const { emitMoveToken, emitUpdateHp, emitAddToken, emitRemoveToken, emitFogUpdate, emitSpellsUpdate, emitAssetsUpdate, emitPing, emitMeasure, emitDiceRoll, emitShareHandout } = useVTTSocket({
    sessionId: activeSession?.id,
    playerName: user?.name || "DM",
    onTokenMoved: (d) => setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === d.token_id ? { ...t, x: d.x, y: d.y } : t) } : p),
    onHpUpdated: (d) => { setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === d.token_id ? { ...t, hp: d.hp } : t) } : p); setSelectedToken(p => p?.id === d.token_id ? { ...p, hp: d.hp } : p); },
    onTokenAdded: (tok) => setActiveSession(p => p ? { ...p, tokens: [...(p.tokens || []), tok] } : p),
    onTokenRemoved: (d) => { setActiveSession(p => p ? { ...p, tokens: p.tokens.filter(t => t.id !== d.token_id) } : p); setSelectedToken(p => p?.id === d.token_id ? null : p); },
    onFogChanged: (d) => setActiveSession(p => p ? { ...p, fog_areas: d.fog_areas } : p),
    onSpellsChanged: (d) => setSpellEffects(d.spells || []),
    onAssetsChanged: (d) => setMapAssetsState(d.assets || []),
    onPingReceived: (d) => {
      const pingId = Date.now() + Math.random();
      setPings(p => [...p, { id: pingId, x: d.x, y: d.y, name: d.player_name }]);
      setTimeout(() => setPings(p => p.filter(pg => pg.id !== pingId)), 4500);
    },
    onShapeDrawn: (d) => {
      if (d.type) { const s = { ...d, id: Date.now() }; setShapes(p => [...p, s]); setTimeout(() => setShapes(p => p.filter(sh => sh.id !== s.id)), 3000); }
    },
    onDiceRolled: (d) => {
      const roll = { id: Date.now(), roller: d.roller, formula: d.formula, rolls: d.rolls, modifier: d.modifier, total: d.total, timestamp: new Date().toLocaleTimeString() };
      setDiceRolls(prev => [roll, ...prev].slice(0, 20));
      toast.info(`${d.roller} rolled ${d.formula}: ${d.total}`);
    },
    onHandoutShared: (d) => {
      setSharedHandout({ name: d.name, content: d.content, image_url: d.image_url });
    },
    onPlayerJoined: (d) => toast.info(`${d.player_name} joined the session`),
    onPlayerLeft: () => {},
  });

  // ==================== DATA LOADING ====================
  useEffect(() => { fetchInitialData(); }, [campaignId]);

  const fetchInitialData = async () => {
    try {
      const [campRes, sessRes, mapRes, npcRes, charRes, itemRes, handoutRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/vtt-sessions`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/maps`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/items`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/handouts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCampaign(campRes.data);
      setSessions(sessRes.data);
      setMaps(mapRes.data);
      setAllNpcs(npcRes.data);
      setAllCharacters(charRes.data);
      setAllItems(itemRes.data);
      setAllHandouts(handoutRes.data);
      // Folders no longer needed (legacy left sidebar removed)
      if (sessRes.data.length > 0) {
        const active = sessRes.data.find(s => s.is_active) || sessRes.data[0];
        await loadSession(active.id);
      }
    } catch { toast.error("Failed to load VTT data"); }
    finally { setLoading(false); }
  };

  const loadSession = async (sessionId) => {
    try {
      const [sesRes, entRes] = await Promise.all([
        axios.get(`${API}/vtt-sessions/${sessionId}`, { withCredentials: true }),
        axios.get(`${API}/vtt-sessions/${sessionId}/entities`, { withCredentials: true }),
      ]);
      setActiveSession(sesRes.data);
      setEntities(entRes.data);
      setSpellEffects(sesRes.data?.spells || []);
      setMapAssetsState(sesRes.data?.assets || []);
      // Phase 3 — lighting state. Honour the saved `lighting_enabled` flag if
      // the DM has ever explicitly set it; otherwise auto-enable Token Vision
      // when walls exist on the map (sensible default for first-time use).
      setWalls(sesRes.data?.walls || []);
      setLightingMode(sesRes.data?.lighting_mode || "persistent");
      setRevealedPolygons(sesRes.data?.revealed_polygons || []);
      if (typeof sesRes.data?.lighting_enabled === "boolean") {
        setLightingEnabled(sesRes.data.lighting_enabled);
      } else {
        setLightingEnabled((sesRes.data?.walls || []).length > 0);
      }
    } catch { toast.error("Failed to load session"); }
  };

  const createSession = async () => {
    try {
      const sel = maps.find(m => m.id === selectedMapId);
      const mapUrl = sel ? (sel.storage_path || sel.image_path || sel.image_url || "") : "";
      const res = await axios.post(`${API}/vtt-sessions`, { campaign_id: campaignId, name: newSessionName, map_id: selectedMapId || null, map_url: mapUrl }, { withCredentials: true });
      setSessions(prev => [res.data, ...prev]);
      await loadSession(res.data.id);
      setCreateDialogOpen(false);
      setNewSessionName("New Session");
      setSelectedMapId("");
      toast.success("Session created");
    } catch { toast.error("Failed to create session"); }
  };

  // ==================== TOKEN ACTIONS ====================
  const addTokenFromEntity = async (entity, type) => {
    if (!activeSession) return;
    const gs = activeSession.grid_size || 50;
    const hp = parseInt(entity.hit_points) || 0;
    const ac = parseInt(entity.armor_class) || 10;
    const existingCount = (activeSession.tokens || []).length;
    const col = existingCount % 6;
    const row = Math.floor(existingCount / 6);
    // Use token_image, then image_url, then image_path as fallback for auto-image
    const tokenImg = entity.token_image || entity.image_url || entity.image_path || "";
    const body = { source_id: entity.id, source_type: type, name: entity.name, x: gs * (3 + col), y: gs * (3 + row), size: SIZE_MAP[entity.size] || 1, hp, max_hp: hp, ac, is_player: type === "character", color: type === "character" ? "#4a9eff" : type === "npc" ? "#c4a55a" : "#ef4444", token_image: tokenImg, layer: activeLayer || "tokens", visible_to_players: activeLayer !== "gm" };
    try {
      const res = await axios.post(`${API}/vtt-sessions/${activeSession.id}/tokens`, body, { withCredentials: true });
      setActiveSession(prev => ({ ...prev, tokens: [...(prev.tokens || []), res.data] }));
      emitAddToken(res.data);
      toast.success(`${entity.name} placed`);
    } catch { toast.error("Failed to add token"); }
  };

  const removeToken = async (tokenId) => {
    if (!activeSession) return;
    try {
      await axios.delete(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { withCredentials: true });
      setActiveSession(prev => ({ ...prev, tokens: prev.tokens.filter(t => t.id !== tokenId) }));
      if (selectedToken?.id === tokenId) setSelectedToken(null);
      emitRemoveToken(tokenId);
      toast.success("Token removed");
    } catch { toast.error("Failed to remove token"); }
  };

  const updateTokenHp = async (tokenId, change) => {
    if (!activeSession) return;
    const token = activeSession.tokens.find(t => t.id === tokenId);
    if (!token) return;
    const prevHp = token.hp;
    const newHp = Math.max(0, Math.min(token.max_hp, token.hp + change));
    if (newHp === prevHp) return;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { hp: newHp }, { withCredentials: true });
      setActiveSession(prev => ({ ...prev, tokens: prev.tokens.map(t => t.id === tokenId ? { ...t, hp: newHp } : t) }));
      setSelectedToken(prev => prev?.id === tokenId ? { ...prev, hp: newHp } : prev);
      emitUpdateHp(tokenId, newHp);
      // Combat log
      const delta = newHp - prevHp;
      if (delta < 0) pushCombatLog("damage", `${token.name} took ${-delta} damage (${newHp}/${token.max_hp})`);
      else if (delta > 0) pushCombatLog("heal", `${token.name} healed ${delta} HP (${newHp}/${token.max_hp})`);
      if (newHp <= 0 && prevHp > 0) pushCombatLog("death", `${token.name} dropped to 0 HP`);
    } catch { toast.error("Failed to update HP"); }
  };

  const moveTokenTo = async (tokenId, x, y) => {
    const gs = activeSession?.grid_size || 50;
    const sx = snapToGrid ? Math.round(x / gs) * gs : x;
    const sy = snapToGrid ? Math.round(y / gs) * gs : y;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { x: sx, y: sy }, { withCredentials: true });
      setActiveSession(prev => ({ ...prev, tokens: prev.tokens.map(t => t.id === tokenId ? { ...t, x: sx, y: sy } : t) }));
      emitMoveToken(tokenId, sx, sy);
    } catch { /* silent */ }
  };

  // ==================== VIEWPORT CONTROLS ====================
  const zoomIn = () => setViewport(v => ({ ...v, scale: Math.min(4, v.scale * 1.25) }));
  const zoomOut = () => setViewport(v => ({ ...v, scale: Math.max(0.2, v.scale / 1.25) }));
  const resetView = () => setViewport({ x: 0, y: 0, scale: 1 });
  const fitToTokens = () => {
    if (!activeSession?.tokens?.length || !canvasAreaRef.current) return;
    const gs = activeSession.grid_size || 50;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    activeSession.tokens.forEach(t => { minX = Math.min(minX, t.x); minY = Math.min(minY, t.y); maxX = Math.max(maxX, t.x + (t.size || 1) * gs); maxY = Math.max(maxY, t.y + (t.size || 1) * gs); });
    const pad = gs * 3;
    const rect = canvasAreaRef.current.getBoundingClientRect();
    const tw = (maxX - minX) + pad * 2;
    const th = (maxY - minY) + pad * 2;
    const scale = Math.min(rect.width / tw, rect.height / th, 2);
    setViewport({ x: (rect.width - tw * scale) / 2 - (minX - pad) * scale, y: (rect.height - th * scale) / 2 - (minY - pad) * scale, scale });
  };

  const toggleGrid = async () => {
    if (!activeSession) return;
    const val = !activeSession.grid_enabled;
    try { await axios.put(`${API}/vtt-sessions/${activeSession.id}`, { grid_enabled: val }, { withCredentials: true }); setActiveSession(p => ({ ...p, grid_enabled: val })); } catch {}
  };

  const updateGridSize = async (size) => {
    if (!activeSession) return;
    setActiveSession(p => ({ ...p, grid_size: size }));
    try { await axios.put(`${API}/vtt-sessions/${activeSession.id}`, { grid_size: size }, { withCredentials: true }); } catch {}
  };

  // Non-passive wheel listener — React's synthetic wheel is passive, so e.preventDefault()
  // can silently fail and lets the page scroll instead of zooming the canvas.
  useEffect(() => {
    const el = canvasAreaRef.current;
    if (!el) return;
    const onWheel = (e) => handleWheel(e);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, castingSpell, selectedSpellId, selectedAssetId, spellDragPreview]);

  const handleWheel = (e) => {
    // If the wheel happened over one of the floating sidebar panels (Tokens / Assets / Spells),
    // let the panel scroll natively instead of zooming the map.
    if (e.target?.closest?.("[data-vtt-sidebar]")) {
      return;
    }
    e.preventDefault();
    // While dragging a spell from the sidebar, scroll rotates the preview
    if (spellDragPreview && (spellDragPreview.spell.shape === "cone" || spellDragPreview.spell.shape === "line")) {
      setSpellRotation(p => p + (e.deltaY < 0 ? -15 : 15));
      return;
    }
    // If casting a directional spell, scroll rotates it
    if (castingSpell && (castingSpell.shape === "cone" || castingSpell.shape === "line")) {
      setSpellRotation(p => p + (e.deltaY < 0 ? -15 : 15));
      return;
    }
    // If a placed spell is selected, scroll rotates it
    if (selectedSpellId) {
      const delta = e.deltaY < 0 ? -15 : 15;
      setSpellEffects(p => {
        const updated = p.map(s => s.id === selectedSpellId ? { ...s, rotation: (s.rotation || 0) + delta } : s);
        saveSpells(updated);
        return updated;
      });
      return;
    }
    // If a placed asset is selected, scroll resizes it (Shift) or rotates (default)
    if (selectedAssetId) {
      if (e.shiftKey) {
        const factor = e.deltaY < 0 ? 1.1 : 0.9;
        setMapAssets(p => p.map(a => a.id === selectedAssetId ? { ...a, size: Math.max(16, Math.min(600, (a.size || 40) * factor)) } : a));
      } else {
        const delta = e.deltaY < 0 ? -15 : 15;
        setMapAssets(p => p.map(a => a.id === selectedAssetId ? { ...a, rotation: (a.rotation || 0) + delta } : a));
      }
      return;
    }
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    setViewport(v => {
      const newScale = Math.max(0.2, Math.min(4, v.scale * factor));
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return { ...v, scale: newScale };
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      return { x: mx - (mx - v.x) * (newScale / v.scale), y: my - (my - v.y) * (newScale / v.scale), scale: newScale };
    });
  };

  // ==================== TOUCH / PINCH-TO-ZOOM ====================
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      pinchRef.current = {
        active: true,
        startDist: Math.hypot(dx, dy),
        startScale: viewport.scale,
        startX: (t1.clientX + t2.clientX) / 2 - (rect?.left || 0),
        startY: (t1.clientY + t2.clientY) / 2 - (rect?.top || 0),
        startVx: viewport.x,
        startVy: viewport.y,
      };
      setIsPanning(false);
    }
  };

  const handleTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0], t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const { startDist, startScale, startX, startY, startVx, startVy } = pinchRef.current;
      const newScale = Math.max(0.2, Math.min(4, startScale * (dist / startDist)));
      const ratio = newScale / startScale;
      setViewport({
        x: startX - (startX - startVx) * ratio,
        y: startY - (startY - startVy) * ratio,
        scale: newScale,
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchRef.current.active = false;
    }
  };

  const getWorldPos = (e) => {
    const rect = canvasAreaRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (e.clientX - rect.left - viewport.x) / viewport.scale, y: (e.clientY - rect.top - viewport.y) / viewport.scale };
  };

  const handleCanvasPointerDown = (e) => {
    // Right-click / middle / shift = pan
    if (e.button === 2 || e.button === 1 || (e.button === 0 && e.shiftKey)) {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = { x: e.clientX, y: e.clientY, vx: viewport.x, vy: viewport.y };
      return;
    }
    if (e.button !== 0) return;
    const pos = getWorldPos(e);
    // Alt+Click anywhere → quick ping (works regardless of active tool)
    if (e.altKey) {
      const pingId = Date.now() + Math.random();
      setPings(p => [...p, { id: pingId, x: pos.x, y: pos.y, name: "You" }]);
      emitPing(pos.x, pos.y);
      setTimeout(() => setPings(p => p.filter(pg => pg.id !== pingId)), 4500);
      return;
    }
    // Ping tool
    if (activeTool === "ping") {
      const pingId = Date.now() + Math.random();
      setPings(p => [...p, { id: pingId, x: pos.x, y: pos.y, name: "You" }]);
      emitPing(pos.x, pos.y);
      setTimeout(() => setPings(p => p.filter(pg => pg.id !== pingId)), 4500);
      return;
    }
    // Spell placement is now drag-only — see handleMapDrop. The legacy click-to-place
    // branch was removed because the sidebar no longer activates castingSpell on click,
    // which prevented accidental double-cast bugs.
    // Map asset placement
    if (placingAsset) {
      addMapAsset(placingAsset, pos.x, pos.y);
      // Stay in place mode until user toggles off (so they can place multiple)
      return;
    }
    // Wall / door / window drawing
    if (activeTool === "wall_add" || activeTool === "door_add" || activeTool === "window_add") {
      const kind = activeTool === "door_add" ? "door" : activeTool === "window_add" ? "window" : "wall";
      if (!wallStart) {
        // First click — anchor the segment start
        setWallStart({ x: pos.x, y: pos.y });
        setWallPreview({ x: pos.x, y: pos.y });
      } else {
        // Second click — commit the segment.
        // Doors default CLOSED (blocking light), windows default OPEN (passing light).
        const seg = {
          id: `w-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          x1: wallStart.x, y1: wallStart.y,
          x2: pos.x, y2: pos.y,
          kind,
          open: kind === "window" ? true : false,
        };
        updateWalls(prev => [...prev, seg]);
        // Continue drawing from the new point so DMs can draw chains quickly
        setWallStart({ x: pos.x, y: pos.y });
        setWallPreview({ x: pos.x, y: pos.y });
        setLightingEnabled(true);
      }
      return;
    }
    // Wall erase — click on a wall/door/window to delete it
    if (activeTool === "wall_erase") {
      const hit = pickWallNear(pos.x, pos.y, walls, 12 / viewport.scale);
      if (hit) deleteWall(hit.id);
      return;
    }
    // Select tool — click on a door or window to toggle it (no tool switch needed)
    if (activeTool === "select") {
      const hit = pickWallNear(pos.x, pos.y, walls, 10 / viewport.scale);
      if (hit && (hit.kind === "door" || hit.kind === "window")) {
        toggleDoor(hit.id);
        return;
      }
    }
    // Fog tools - start painting
    if (activeTool === "fog_add" || activeTool === "fog_remove") {
      setIsFogPainting(true);
      if (activeTool === "fog_add") paintFogAt(pos.x, pos.y);
      else eraseFogAt(pos.x, pos.y);
      return;
    }
    // Fog rectangle - start drag
    if (activeTool === "fog_rect") {
      setFogRectStart(pos);
      setFogRectPreview({ x: pos.x, y: pos.y, width: 0, height: 0 });
      return;
    }
    // Measurement tools
    if (activeTool?.startsWith("measure_")) {
      setDrawStart(pos);
      return;
    }
    // Select tool - start drag-selection box (if not clicking on a token)
    if (activeTool === "select" && e.target === canvasAreaRef.current?.querySelector('[data-testid="vtt-viewport"]')) {
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
        setSelectedToken(null);
        setSelectedTokenIds([]);
        setSelectedSpellId(null);
        setSelectedAssetId(null);
        setSelectedAssetIds([]);
      }
      setContextMenu(null);
      setSelectionBox({ startX: pos.x, startY: pos.y, endX: pos.x, endY: pos.y });
    }
  };

  const handleCanvasPointerMove = (e) => {
    if (isPanning) {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      setViewport(v => ({ ...v, x: panStartRef.current.vx + dx, y: panStartRef.current.vy + dy }));
      return;
    }
    // Spell effect drag
    if (spellDragRef.current.active) {
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return;
      const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
      const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
      const nx = wx - spellDragRef.current.offsetX;
      const ny = wy - spellDragRef.current.offsetY;
      setSpellEffects(p => p.map(s => s.id === spellDragRef.current.id ? { ...s, x: nx, y: ny } : s));
      return;
    }
    // Map asset drag (single OR group)
    if (assetDragRef.current.active) {
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return;
      const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
      const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
      const nx = wx - assetDragRef.current.offsetX;
      const ny = wy - assetDragRef.current.offsetY;
      const groupIds = assetDragRef.current.groupIds;
      if (groupIds && groupIds.length > 1) {
        // Compute delta from current dragged anchor and apply to every group member
        const anchorOrig = assetDragRef.current.anchorOrig;
        const dx = nx - anchorOrig.x;
        const dy = ny - anchorOrig.y;
        const origPositions = assetDragRef.current.origPositions;
        setMapAssetsState(p => p.map(a => {
          if (!groupIds.includes(a.id)) return a;
          const o = origPositions[a.id];
          if (!o) return a;
          return { ...a, x: o.x + dx, y: o.y + dy };
        }));
      } else {
        setMapAssetsState(p => p.map(a => a.id === assetDragRef.current.id ? { ...a, x: nx, y: ny } : a));
      }
      return;
    }
    // Resize handle drag
    if (resizeToken) {
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return;
      const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
      const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
      const g = activeSession?.grid_size || 50;
      const tok = activeSession?.tokens?.find(t => t.id === resizeToken.id);
      if (!tok) return;
      const dx = wx - tok.x, dy = wy - tok.y;
      const maxD = Math.max(dx, dy);
      const newSize = Math.max(0.5, Math.round(maxD / g * 2) / 2); // snap to 0.5 increments
      setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === resizeToken.id ? { ...t, size: newSize } : t) } : p);
      return;
    }
    if (dragToken) {
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return;
      const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
      const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
      const g = activeSession?.grid_size || 50;
      // Wall-collision clamp helper. Given a token's current top-left (tx,ty),
      // a proposed top-left (nx,ny), and the token's pixel size, return the
      // furthest legal top-left along that movement that doesn't cross a wall
      // (open doors/windows let the token through). The path is checked at the
      // token's CENTRE — close enough for typical token sizes.
      const clampMoveAgainstWalls = (tx, ty, nx, ny, sizePx) => {
        if (!walls || walls.length === 0) return { x: nx, y: ny };
        const halfTok = sizePx / 2;
        const cx1 = tx + halfTok, cy1 = ty + halfTok;
        const cx2 = nx + halfTok, cy2 = ny + halfTok;
        const hit = segmentBlockedByWall(cx1, cy1, cx2, cy2, walls);
        if (!hit) return { x: nx, y: ny };
        // Translate the clamped centre back to a top-left position.
        return { x: hit.x - halfTok, y: hit.y - halfTok };
      };
      // Multi-select: move all selected tokens by the same delta
      if (selectedTokenIds.length > 1 && selectedTokenIds.includes(dragToken)) {
        const tok = activeSession?.tokens?.find(t => t.id === dragToken);
        if (!tok) return;
        const proposedX = wx - g / 2;
        const proposedY = wy - g / 2;
        const tokSize = g * (tok.size || 1);
        const clamped = clampMoveAgainstWalls(tok.x, tok.y, proposedX, proposedY, tokSize);
        const deltaX = clamped.x - tok.x;
        const deltaY = clamped.y - tok.y;
        setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => selectedTokenIds.includes(t.id) ? { ...t, x: t.x + deltaX, y: t.y + deltaY } : t) } : p);
      } else {
        const tok = activeSession?.tokens?.find(t => t.id === dragToken);
        if (!tok) return;
        const proposedX = wx - g / 2;
        const proposedY = wy - g / 2;
        const tokSize = g * (tok.size || 1);
        const clamped = clampMoveAgainstWalls(tok.x, tok.y, proposedX, proposedY, tokSize);
        setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === dragToken ? { ...t, x: clamped.x, y: clamped.y } : t) } : p);
      }
      return;
    }
    // Fog brush/eraser painting while dragging
    if (isFogPainting && (activeTool === "fog_add" || activeTool === "fog_remove")) {
      const pos = getWorldPos(e);
      if (activeTool === "fog_add") paintFogAt(pos.x, pos.y);
      else eraseFogAt(pos.x, pos.y);
      return;
    }
    // Wall drawing — live preview line from anchor to cursor
    if ((activeTool === "wall_add" || activeTool === "door_add" || activeTool === "window_add") && wallStart) {
      const pos = getWorldPos(e);
      setWallPreview({ x: pos.x, y: pos.y });
      return;
    }
    // Fog rectangle drag preview
    if (fogRectStart) {
      const pos = getWorldPos(e);
      setFogRectPreview({
        x: Math.min(fogRectStart.x, pos.x),
        y: Math.min(fogRectStart.y, pos.y),
        width: Math.abs(pos.x - fogRectStart.x),
        height: Math.abs(pos.y - fogRectStart.y),
      });
      return;
    }
    if (drawStart && activeTool?.startsWith("measure_")) {
      const pos = getWorldPos(e);
      setDrawPreview({ startX: drawStart.x, startY: drawStart.y, endX: pos.x, endY: pos.y, type: activeTool });
    }
    // Selection box drag update
    if (selectionBox) {
      const pos = getWorldPos(e);
      setSelectionBox(b => b ? { ...b, endX: pos.x, endY: pos.y } : b);
    }
  };

  const handleCanvasPointerUp = (e) => {
    setIsPanning(false);
    // Finish spell drag → persist new position
    if (spellDragRef.current.active) {
      spellDragRef.current = { active: false, id: null, offsetX: 0, offsetY: 0 };
      // Use setTimeout to persist with latest spellEffects state after React commits
      setTimeout(() => setSpellEffects(curr => { saveSpells(curr); return curr; }), 0);
      return;
    }
    // Finish asset drag → persist
    if (assetDragRef.current.active) {
      assetDragRef.current = { active: false, id: null, offsetX: 0, offsetY: 0 };
      setTimeout(() => setMapAssetsState(curr => { saveAssets(curr); return curr; }), 0);
      return;
    }
    // Stop fog painting
    if (isFogPainting) {
      setIsFogPainting(false);
      // Force final save
      if (activeSession) {
        if (fogSaveTimerRef.current) clearTimeout(fogSaveTimerRef.current);
        const fogAreas = activeSession.fog_areas || [];
        axios.put(`${API}/vtt-sessions/${activeSession.id}/fog`, { fog_areas: fogAreas }, { withCredentials: true })
          .then(() => emitFogUpdate(fogAreas)).catch(() => {});
      }
      return;
    }
    // Commit fog rectangle
    if (fogRectStart && fogRectPreview) {
      const { x, y, width, height } = fogRectPreview;
      if (width > 5 && height > 5) {
        const newRect = { id: crypto.randomUUID(), x, y, width, height };
        setActiveSession(p => {
          const updated = [...(p.fog_areas || []), newRect];
          debouncedFogSave(updated);
          return { ...p, fog_areas: updated };
        });
      }
      setFogRectStart(null);
      setFogRectPreview(null);
      return;
    }
    // Finish resize
    if (resizeToken) {
      const token = activeSession?.tokens?.find(t => t.id === resizeToken.id);
      if (token) updateTokenSize(resizeToken.id, token.size);
      setResizeToken(null);
      return;
    }
    if (dragToken) {
      // Multi-select: persist all moved tokens
      if (selectedTokenIds.length > 1 && selectedTokenIds.includes(dragToken)) {
        selectedTokenIds.forEach(tid => {
          const tok = activeSession?.tokens?.find(t => t.id === tid);
          if (tok) moveTokenTo(tid, tok.x, tok.y);
        });
      } else {
        const token = activeSession?.tokens?.find(t => t.id === dragToken);
        if (token) moveTokenTo(dragToken, token.x, token.y);
      }
      setDragToken(null);
      return;
    }
    if (drawStart) {
      const pos = getWorldPos(e);
      const dx = Math.abs(pos.x - drawStart.x);
      const dy = Math.abs(pos.y - drawStart.y);
      if (dx > 5 || dy > 5) {
        if (activeTool?.startsWith("measure_")) {
          const shape = { id: Date.now(), startX: drawStart.x, startY: drawStart.y, endX: pos.x, endY: pos.y, type: activeTool };
          setShapes(s => [...s, shape]);
          emitMeasure(shape);
          if (!measurePersistent) setTimeout(() => setShapes(s => s.filter(sh => sh.id !== shape.id)), 3000);
        }
      }
      setDrawStart(null);
      setDrawPreview(null);
    }
    // Finish drag-select box → select all tokens AND assets in current layer inside box
    if (selectionBox) {
      const x1 = Math.min(selectionBox.startX, selectionBox.endX);
      const y1 = Math.min(selectionBox.startY, selectionBox.endY);
      const x2 = Math.max(selectionBox.startX, selectionBox.endX);
      const y2 = Math.max(selectionBox.startY, selectionBox.endY);
      if (Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5) {
        const gs = activeSession?.grid_size || 50;
        const additive = e.ctrlKey || e.metaKey || e.shiftKey;
        // Tokens — only those on the active layer
        const insideTokens = (activeSession?.tokens || []).filter(t => {
          if ((t.layer || "tokens") !== activeLayer) return false;
          const sz = (t.size || 1) * gs;
          const cx = t.x + sz / 2;
          const cy = t.y + sz / 2;
          return cx >= x1 && cx <= x2 && cy >= y1 && cy <= y2;
        }).map(t => t.id);
        // Map assets — only those on the active layer
        const insideAssets = (mapAssets || []).filter(a => {
          if ((a.layer || "tokens") !== activeLayer) return false;
          return a.x >= x1 && a.x <= x2 && a.y >= y1 && a.y <= y2;
        }).map(a => a.id);
        if (insideTokens.length > 0 || insideAssets.length > 0) {
          setSelectedTokenIds(prev => additive ? [...new Set([...prev, ...insideTokens])] : insideTokens);
          setSelectedAssetIds(prev => additive ? [...new Set([...prev, ...insideAssets])] : insideAssets);
          // Clear single-asset panel when bulk-selecting many
          if (insideAssets.length > 1) setSelectedAssetId(null);
          else if (insideAssets.length === 1) setSelectedAssetId(insideAssets[0]);
          const total = insideTokens.length + insideAssets.length;
          const parts = [];
          if (insideTokens.length) parts.push(`${insideTokens.length} token${insideTokens.length === 1 ? "" : "s"}`);
          if (insideAssets.length) parts.push(`${insideAssets.length} asset${insideAssets.length === 1 ? "" : "s"}`);
          toast.info(`${parts.join(" + ")} selected`, { duration: 1500 });
          if (total === 0) toast.dismiss();
        } else if (!additive) {
          setSelectedTokenIds([]);
          setSelectedAssetIds([]);
        }
      }
      setSelectionBox(null);
    }
  };

  const clearShapes = () => setShapes([]);

  // ==================== FOG BRUSH/ERASER ====================
  const debouncedFogSave = useCallback((fogAreas) => {
    if (fogSaveTimerRef.current) clearTimeout(fogSaveTimerRef.current);
    fogSaveTimerRef.current = setTimeout(() => {
      if (!activeSession) return;
      axios.put(`${API}/vtt-sessions/${activeSession.id}/fog`, { fog_areas: fogAreas }, { withCredentials: true })
        .then(() => emitFogUpdate(fogAreas))
        .catch(() => {});
    }, 300);
  }, [activeSession?.id]);

  const paintFogAt = (x, y) => {
    if (!activeSession) return;
    const r = fogBrushSize;
    const newDot = { id: crypto.randomUUID(), cx: x, cy: y, r };
    setActiveSession(p => {
      const updated = [...(p.fog_areas || []), newDot];
      debouncedFogSave(updated);
      return { ...p, fog_areas: updated };
    });
  };

  const eraseFogAt = (x, y) => {
    if (!activeSession) return;
    const er = fogBrushSize;
    setActiveSession(p => {
      const original = p.fog_areas || [];
      const survivors = [];
      const newDots = [];
      for (const f of original) {
        // Brush dot — remove if within range
        if (f.cx !== undefined) {
          const dist = Math.sqrt((f.cx - x) ** 2 + (f.cy - y) ** 2);
          if (dist > (f.r + er) * 0.6) survivors.push(f);
          continue;
        }
        // Rectangle — if eraser is OUTSIDE it (plus brush radius), keep as-is.
        // Otherwise tessellate into dots so subsequent strokes can chip away.
        if (f.width !== undefined) {
          const cx = f.x + f.width / 2;
          const cy = f.y + f.height / 2;
          const insideX = x >= f.x - er && x <= f.x + f.width + er;
          const insideY = y >= f.y - er && y <= f.y + f.height + er;
          if (!insideX || !insideY) {
            survivors.push(f);
            continue;
          }
          // Tessellate this rectangle into dots, then erase any intersecting the brush.
          const dotR = Math.max(20, er);
          const step = dotR * 1.4;
          for (let py = f.y + dotR; py <= f.y + f.height; py += step) {
            for (let px = f.x + dotR; px <= f.x + f.width; px += step) {
              const dist = Math.sqrt((px - x) ** 2 + (py - y) ** 2);
              if (dist > (dotR + er) * 0.6) {
                newDots.push({ id: crypto.randomUUID(), cx: px, cy: py, r: dotR });
              }
            }
          }
          // Also fill any sliver along the right/bottom edge so we don't leave a hole
          // (this is approximate but the visual difference is hidden by overlapping dots)
          const rightCol = f.x + f.width - dotR;
          const bottomRow = f.y + f.height - dotR;
          if (rightCol > 0) {
            for (let py = f.y + dotR; py <= f.y + f.height; py += step) {
              const dist = Math.sqrt((rightCol - x) ** 2 + (py - y) ** 2);
              if (dist > (dotR + er) * 0.6) {
                newDots.push({ id: crypto.randomUUID(), cx: rightCol, cy: py, r: dotR });
              }
            }
          }
          if (bottomRow > 0) {
            for (let px = f.x + dotR; px <= f.x + f.width; px += step) {
              const dist = Math.sqrt((px - x) ** 2 + (bottomRow - y) ** 2);
              if (dist > (dotR + er) * 0.6) {
                newDots.push({ id: crypto.randomUUID(), cx: px, cy: bottomRow, r: dotR });
              }
            }
          }
          // Don't push the rectangle back — it's been replaced by dots
          // Suppress unused-var warning
          void cx; void cy;
          continue;
        }
        survivors.push(f);
      }
      const updated = [...survivors, ...newDots];
      debouncedFogSave(updated);
      return { ...p, fog_areas: updated };
    });
  };

  // ==================== SPELL EFFECTS PERSISTENCE ====================
  const saveSpells = useCallback((spells) => {
    if (spellsSaveTimerRef.current) clearTimeout(spellsSaveTimerRef.current);
    spellsSaveTimerRef.current = setTimeout(() => {
      if (!activeSession) return;
      axios.put(`${API}/vtt-sessions/${activeSession.id}/spells`, { spells }, { withCredentials: true })
        .then(() => emitSpellsUpdate(spells))
        .catch(() => {});
    }, 200);
  }, [activeSession?.id, emitSpellsUpdate]);

  const addSpellEffect = (spell, x, y, rotation) => {
    const effect = { id: crypto.randomUUID(), spell, x, y, rotation };
    setSpellEffects(p => {
      const updated = [...p, effect];
      saveSpells(updated);
      return updated;
    });
  };

  const removeSpellEffect = (id) => {
    setSpellEffects(p => {
      const updated = p.filter(s => s.id !== id);
      saveSpells(updated);
      return updated;
    });
  };

  const updateSpellPosition = (id, x, y) => {
    setSpellEffects(p => {
      const updated = p.map(s => s.id === id ? { ...s, x, y } : s);
      saveSpells(updated);
      return updated;
    });
  };

  const clearSpellEffects = () => {
    setSpellEffects([]);
    saveSpells([]);
  };

  // ==================== MAP ASSET PERSISTENCE ====================
  const saveAssets = useCallback((assets) => {
    if (assetsSaveTimerRef.current) clearTimeout(assetsSaveTimerRef.current);
    assetsSaveTimerRef.current = setTimeout(() => {
      if (!activeSession) return;
      axios.put(`${API}/vtt-sessions/${activeSession.id}/assets`, { assets }, { withCredentials: true })
        .then(() => emitAssetsUpdate(assets))
        .catch(() => {});
    }, 200);
  }, [activeSession?.id, emitAssetsUpdate]);

  const setMapAssets = (updater) => {
    setMapAssetsState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveAssets(next);
      return next;
    });
  };

  // Phase 3 — Persistent reveal: every time tokens move or walls change, sample the
  // current LOS polygons and append them to `revealedPolygons`. The fog mask renders
  // both the live LOS holes AND the accumulated reveals so players see "previously
  // explored" areas dimly even after they leave.
  useEffect(() => {
    if (!lightingEnabled || lightingMode !== "persistent") return;
    if (!activeSession?.tokens) return;
    const sources = tokenVisionSources(activeSession.tokens, gs / 5);
    if (sources.length === 0) return;
    const newPaths = sources.map(s => polygonToPath(computeVisibilityPolygon(s.x, s.y, s.radius, walls)));
    setRevealedPolygons(prev => {
      const merged = [...prev, ...newPaths.map(p => ({ path: p, ts: Date.now() }))];
      const trimmed = merged.length > 240 ? merged.slice(merged.length - 240) : merged;
      saveRevealedPolygons(trimmed);
      return trimmed;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lightingEnabled,
    lightingMode,
    walls,
    (activeSession?.tokens || []).map(t => `${t.id}:${Math.round(t.x)}:${Math.round(t.y)}:${t.size || 1}:${t.vision_bright || 0}:${t.vision_dim || 0}:${t.vision_dark || 0}:${t.vision_radius || 0}`).join("|"),
  ]);

  // ==================== WALL / LIGHTING PERSISTENCE (Phase 3) ====================
  const saveWalls = useCallback((nextWalls) => {
    if (wallsSaveTimerRef.current) clearTimeout(wallsSaveTimerRef.current);
    wallsSaveTimerRef.current = setTimeout(() => {
      if (!activeSession) return;
      axios.put(`${API}/vtt-sessions/${activeSession.id}/walls`, { walls: nextWalls }, { withCredentials: true }).catch(() => {});
    }, 250);
  }, [activeSession?.id]);

  const updateWalls = (updater) => {
    setWalls(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveWalls(next);
      return next;
    });
  };

  const updateLightingMode = (mode) => {
    setLightingMode(mode);
    if (!activeSession) return;
    axios.put(`${API}/vtt-sessions/${activeSession.id}/lighting`, { lighting_mode: mode }, { withCredentials: true }).catch(() => {});
  };

  // Top-level Vision Mode toggle. `enabled=true` ⇒ Token Vision (dynamic LOS).
  // `enabled=false` ⇒ Fog of War (DM-painted manual fog). Persists to backend.
  const updateVisionMode = (enabled) => {
    setLightingEnabled(enabled);
    if (!activeSession) return;
    axios.put(`${API}/vtt-sessions/${activeSession.id}/lighting`, { lighting_enabled: enabled }, { withCredentials: true }).catch(() => {});
  };

  const saveRevealedPolygons = useCallback((polys) => {
    if (revealedSaveTimerRef.current) clearTimeout(revealedSaveTimerRef.current);
    revealedSaveTimerRef.current = setTimeout(() => {
      if (!activeSession) return;
      axios.put(`${API}/vtt-sessions/${activeSession.id}/lighting`, { revealed_polygons: polys }, { withCredentials: true }).catch(() => {});
    }, 600);
  }, [activeSession?.id]);

  const toggleDoor = (wallId) => {
    updateWalls(prev => prev.map(w => w.id === wallId ? { ...w, open: !w.open } : w));
  };

  const deleteWall = (wallId) => {
    updateWalls(prev => prev.filter(w => w.id !== wallId));
  };

  const clearAllWalls = () => {
    updateWalls([]);
    setLightingEnabled(false);
  };

  // Phase 3 — Light-emitter presets. When the user places a known light source asset,
  // pre-populate its bright/dim radii (in feet) so it just works without a config dialog.
  const LIGHT_PRESETS = {
    "campfire":     { bright: 20, dim: 20 },
    "torch":        { bright: 20, dim: 20 },
    "torch-sconce": { bright: 20, dim: 20 },
    "lantern":      { bright: 30, dim: 30 },
    "candle":       { bright: 5,  dim: 5  },
    "brazier":      { bright: 30, dim: 30 },
  };

  const addMapAsset = (assetKey, x, y) => {
    // Default asset size = 1 grid square (D&D Medium creature = 5ft = 1 square).
    const gridSz = activeSession?.grid_size || 50;
    const preset = LIGHT_PRESETS[assetKey] || null;
    const base = { id: crypto.randomUUID(), asset_key: assetKey, x, y, size: gridSz, rotation: 0, layer: activeLayer || "tokens" };
    if (preset) {
      base.light_bright = preset.bright;
      base.light_dim = preset.dim;
    }
    setMapAssets(p => [...p, base]);
  };

  const removeMapAsset = (id) => {
    setMapAssets(p => p.filter(a => a.id !== id));
  };

  const handleTokenDoubleClick = async (token) => {
    // Open full stat block panel - fetch full entity data
    setTokenInfoOpen(token);
    setSelectedToken(token);
    setFullStatBlock(null);
    if (token.source_id && token.source_type) {
      try {
        const typeMap = { creature: "creatures", npc: "npcs", character: "characters" };
        const endpoint = typeMap[token.source_type];
        if (endpoint) {
          const res = await axios.get(`${API}/${endpoint}/${token.source_id}`, { withCredentials: true });
          setFullStatBlock(res.data);
        }
      } catch { /* full stat block unavailable */ }
    }
  };

  // Conditions management
  const toggleCondition = async (tokenId, condName) => {
    if (!activeSession) return;
    const token = activeSession.tokens.find(t => t.id === tokenId);
    if (!token) return;
    const current = token.conditions || [];
    const updated = current.includes(condName) ? current.filter(c => c !== condName) : [...current, condName];
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { conditions: updated }, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, conditions: updated } : t) }));
      setTokenInfoOpen(p => p?.id === tokenId ? { ...p, conditions: updated } : p);
      setSelectedToken(p => p?.id === tokenId ? { ...p, conditions: updated } : p);
      const wasAdded = updated.length > current.length;
      pushCombatLog("condition", `${token.name} ${wasAdded ? "is now" : "no longer"} ${condName}`);
    } catch { toast.error("Failed to update conditions"); }
  };

  // Token resize
  const updateTokenSize = async (tokenId, newSize) => {
    if (!activeSession) return;
    const clamped = Math.max(0.5, Math.min(6, newSize));
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { size: clamped }, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, size: clamped } : t) }));
      setSelectedToken(p => p?.id === tokenId ? { ...p, size: clamped } : p);
      setTokenInfoOpen(p => p?.id === tokenId ? { ...p, size: clamped } : p);
    } catch { /* ignore */ }
  };

  // Per-token vision update — writes any subset of vision_bright / vision_dim /
  // vision_dark (all in feet) to the backend and mirrors it into local state so
  // the lighting engine's reveal polygon rerenders immediately.
  const updateTokenVision = async (tokenId, patch) => {
    if (!activeSession) return;
    // Sanitise: clamp to [0, 999] and coerce to numbers.
    const clean = {};
    for (const k of ["vision_bright", "vision_dim", "vision_dark"]) {
      if (k in patch) {
        const n = Number(patch[k]);
        clean[k] = Number.isFinite(n) ? Math.max(0, Math.min(999, n)) : 0;
      }
    }
    if (Object.keys(clean).length === 0) return;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, clean, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, ...clean } : t) }));
      setSelectedToken(p => p?.id === tokenId ? { ...p, ...clean } : p);
    } catch { toast.error("Failed to update vision"); }
  };

  // ==================== TOKEN ANIMATIONS ====================
  // Trigger a transient animation class on a token ("attack", "damage", "heal", "death")
  const triggerTokenAnim = useCallback((tokenId, type, duration = 600) => {
    if (!tokenId) return;
    setTokenAnim(p => ({ ...p, [tokenId]: type }));
    const existing = tokenAnimTimersRef.current[tokenId];
    if (existing) clearTimeout(existing);
    tokenAnimTimersRef.current[tokenId] = setTimeout(() => {
      setTokenAnim(p => { const { [tokenId]: _, ...rest } = p; return rest; });
      delete tokenAnimTimersRef.current[tokenId];
    }, duration);
  }, []);

  // Auto-detect HP drops (damage), HP rises (heal) and is_dead transitions (death)
  useEffect(() => {
    const tokens = activeSession?.tokens || [];
    const prev = prevTokensRef.current;
    tokens.forEach(t => {
      const p = prev[t.id];
      if (p) {
        if (!p.is_dead && t.is_dead) triggerTokenAnim(t.id, "death", 900);
        else if (typeof t.hp === "number" && typeof p.hp === "number") {
          if (t.hp < p.hp) triggerTokenAnim(t.id, "damage", 600);
          else if (t.hp > p.hp) triggerTokenAnim(t.id, "heal", 700);
        }
      }
      prev[t.id] = { hp: t.hp, is_dead: t.is_dead };
    });
    // Clean up removed tokens
    Object.keys(prev).forEach(id => { if (!tokens.find(t => t.id === id)) delete prev[id]; });
  }, [activeSession?.tokens, triggerTokenAnim]);

  // Toggle dead state on a token
  const toggleDead = async (tokenId) => {
    if (!activeSession) return;
    const token = activeSession.tokens.find(t => t.id === tokenId);
    if (!token) return;
    const isDead = !token.is_dead;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { is_dead: isDead, hp: isDead ? 0 : token.hp }, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, is_dead: isDead, hp: isDead ? 0 : t.hp } : t) }));
      setSelectedToken(p => p?.id === tokenId ? { ...p, is_dead: isDead } : p);
      setTokenInfoOpen(p => p?.id === tokenId ? { ...p, is_dead: isDead } : p);
      pushCombatLog("death", `${token.name} ${isDead ? "marked dead" : "revived"}`);
      toast.success(isDead ? `${token.name} marked dead` : `${token.name} revived`);
    } catch {}
  };

  // Change a token's layer (DM/GM/Map)
  const changeTokenLayer = async (tokenId, layer) => {
    if (!activeSession) return;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { layer, visible_to_players: layer === "tokens" }, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, layer, visible_to_players: layer === "tokens" } : t) }));
      toast.success(`Moved to ${layer} layer`);
    } catch {}
  };

  // Toggle player visibility on a token
  const setTokenVisibility = async (tokenId, vis) => {
    if (!activeSession) return;
    try {
      await axios.put(`${API}/vtt-sessions/${activeSession.id}/tokens/${tokenId}`, { visible_to_players: vis }, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: p.tokens.map(t => t.id === tokenId ? { ...t, visible_to_players: vis } : t) }));
      toast.success(vis ? "Token revealed to players" : "Token hidden from players");
    } catch {}
  };

  // Right-click context menu handler for tokens — clamps to viewport so menu never clips off-screen
  const handleTokenContextMenu = (e, token) => {
    e.preventDefault();
    e.stopPropagation();
    const MENU_W = 200;
    const MENU_H = 420;
    const pad = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = Math.min(e.clientX, vw - MENU_W - pad);
    const y = Math.min(e.clientY, vh - MENU_H - pad);
    setContextMenu({ x: Math.max(pad, x), y: Math.max(pad, y), token });
  };

  // Initiative tracker helpers
  const addToInitiative = (token, init) => {
    setInitiativeOrder(p => {
      const filtered = p.filter(i => i.id !== token.id);
      return [...filtered, { id: token.id, name: token.name, initiative: init, isActive: false }].sort((a, b) => b.initiative - a.initiative);
    });
    setShowInitTracker(true);
    pushCombatLog("init", `${token.name} added to initiative (${init})`);
  };
  const removeFromInitiative = (tokenId) => setInitiativeOrder(p => p.filter(i => i.id !== tokenId));
  const setActiveInitiative = (idx) => setInitiativeOrder(p => p.map((i, n) => ({ ...i, isActive: n === idx })));

  // Clipboard for token copy/paste
  const [clipboardToken, setClipboardToken] = useState(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!activeSession) return;
      // Ignore if user is typing in an input
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      // Escape key - cancel placement modes
      if (e.key === "Escape") {
        if (wallStart) { setWallStart(null); setWallPreview(null); return; }
        if (placingAsset) { setPlacingAsset(null); toast.info("Asset placement cancelled"); return; }
        if (castingSpell) { setCastingSpell(null); setSpellRotation(0); toast.info("Casting cancelled"); return; }
        if (selectedAssetIds.length > 0) { setSelectedAssetIds([]); setSelectedAssetId(null); return; }
        if (selectedAssetId) { setSelectedAssetId(null); return; }
        if (selectedSpellId) { setSelectedSpellId(null); return; }
      }

      // Delete key - remove selected token(s) or selected spell or selected asset(s)
      if (e.key === "Delete" || e.key === "Backspace") {
        // Bulk-delete from rubber-band: tokens + assets at once
        if (selectedAssetIds.length > 0) {
          selectedAssetIds.forEach(id => removeMapAsset(id));
          selectedTokenIds.forEach(id => removeToken(id));
          const total = selectedAssetIds.length + selectedTokenIds.length;
          toast.success(`Removed ${total} item${total === 1 ? "" : "s"}`);
          setSelectedAssetIds([]);
          setSelectedTokenIds([]);
          setSelectedAssetId(null);
          return;
        }
        if (selectedAssetId) {
          removeMapAsset(selectedAssetId);
          setSelectedAssetId(null);
          return;
        }
        if (selectedSpellId) {
          removeSpellEffect(selectedSpellId);
          setSelectedSpellId(null);
          return;
        }
        if (selectedTokenIds.length > 0) {
          selectedTokenIds.forEach(id => removeToken(id));
          setSelectedTokenIds([]);
          setSelectedToken(null);
        } else if (selectedToken) {
          removeToken(selectedToken.id);
          setSelectedToken(null);
        }
        return;
      }

      // [ and ] - rotate selected spell (5° fine, 15° with shift)
      if (selectedSpellId && (e.key === "[" || e.key === "]")) {
        const step = e.shiftKey ? 15 : 5;
        const delta = e.key === "[" ? -step : step;
        setSpellEffects(p => {
          const updated = p.map(s => s.id === selectedSpellId ? { ...s, rotation: (s.rotation || 0) + delta } : s);
          saveSpells(updated);
          return updated;
        });
        e.preventDefault();
        return;
      }

      // Ctrl+C - copy selected token
      if ((e.ctrlKey || e.metaKey) && e.key === "c" && selectedToken) {
        setClipboardToken({ ...selectedToken });
        toast.info(`Copied ${selectedToken.name}`);
        e.preventDefault();
        return;
      }

      // Ctrl+V - paste copied token
      if ((e.ctrlKey || e.metaKey) && e.key === "v" && clipboardToken) {
        const gs = activeSession.grid_size || 50;
        const body = {
          source_id: clipboardToken.source_id, source_type: clipboardToken.source_type,
          name: clipboardToken.name, x: clipboardToken.x + gs, y: clipboardToken.y + gs,
          size: clipboardToken.size, hp: clipboardToken.max_hp, max_hp: clipboardToken.max_hp,
          ac: clipboardToken.ac, is_player: clipboardToken.is_player,
          color: clipboardToken.color, token_image: clipboardToken.token_image || "",
          layer: clipboardToken.layer || "tokens", visible_to_players: clipboardToken.visible_to_players !== false,
        };
        axios.post(`${API}/vtt-sessions/${activeSession.id}/tokens`, body, { withCredentials: true })
          .then(res => {
            setActiveSession(p => ({ ...p, tokens: [...(p.tokens || []), res.data] }));
            emitAddToken(res.data);
            toast.success(`Pasted ${clipboardToken.name}`);
          }).catch(() => toast.error("Failed to paste"));
        e.preventDefault();
        return;
      }

      // Arrow keys - fine-move selected token
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) && selectedToken) {
        const step = e.shiftKey ? 1 : (activeSession.grid_size || 50);
        const dx = e.key === "ArrowRight" ? step : e.key === "ArrowLeft" ? -step : 0;
        const dy = e.key === "ArrowDown" ? step : e.key === "ArrowUp" ? -step : 0;
        let newX = selectedToken.x + dx;
        let newY = selectedToken.y + dy;
        // Wall collision: clamp the keyboard nudge at the nearest blocking wall
        // (open doors / windows are passable). Operates on the token centre.
        if (walls && walls.length > 0) {
          const gs2 = activeSession.grid_size || 50;
          const tokSizePx = gs2 * (selectedToken.size || 1);
          const halfTok = tokSizePx / 2;
          const cx1 = selectedToken.x + halfTok, cy1 = selectedToken.y + halfTok;
          const cx2 = newX + halfTok,            cy2 = newY + halfTok;
          const hit = segmentBlockedByWall(cx1, cy1, cx2, cy2, walls);
          if (hit) {
            newX = hit.x - halfTok;
            newY = hit.y - halfTok;
            toast.info("Blocked by a wall");
          }
        }
        setActiveSession(p => p ? { ...p, tokens: p.tokens.map(t => t.id === selectedToken.id ? { ...t, x: newX, y: newY } : t) } : p);
        setSelectedToken(p => p ? { ...p, x: newX, y: newY } : p);
        moveTokenTo(selectedToken.id, newX, newY);
        e.preventDefault();
        return;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeSession, selectedToken, selectedTokenIds, clipboardToken, selectedSpellId, selectedAssetId, selectedAssetIds, placingAsset, castingSpell, walls]);

  const startSession = () => {
    setSessionActive(!sessionActive);
    toast.success(sessionActive ? "Session paused" : "Session started! Players can now connect.");
  };

  const rollDice = (formula, rollerName) => {
    const match = formula.match(/(\d+)d(\d+)([+-]\d+)?/);
    if (!match) { toast.error("Invalid dice formula"); return; }
    const count = parseInt(match[1]);
    const sides = parseInt(match[2]);
    const modifier = match[3] ? parseInt(match[3]) : 0;
    let total = modifier;
    const rolls = [];
    for (let i = 0; i < count; i++) {
      const r = Math.floor(Math.random() * sides) + 1;
      rolls.push(r);
      total += r;
    }
    const roller = rollerName || "DM";
    const roll = { id: Date.now(), roller, formula, rolls, modifier, total, timestamp: new Date().toLocaleTimeString() };
    setDiceRolls(prev => [roll, ...prev].slice(0, 20));
    // Emit to all connected players
    emitDiceRoll({ roller, formula, rolls, modifier, total });
    // Save to campaign rolls endpoint
    axios.post(`${API}/campaigns/${campaignId}/rolls`, { dice_type: `d${sides}`, result: total, roller }, { withCredentials: true }).catch(() => {});
    return roll;
  };

  // ==================== MAP DROP ====================
  // Helper: turn a TokenBrowser payload into the same body shape as legacy drops use.
  const placeTokenFromBrowser = async (browserItem, sourceType, dropPos) => {
    if (!activeSession) return;
    const gs = activeSession.grid_size || 50;
    const center = dropPos || (() => {
      // Drop in centre of viewport
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return { x: 100, y: 100 };
      return {
        x: (rect.width / 2 - viewport.x) / viewport.scale,
        y: (rect.height / 2 - viewport.y) / viewport.scale,
      };
    })();
    const sx = snapToGrid ? Math.round(center.x / gs) * gs : center.x;
    const sy = snapToGrid ? Math.round(center.y / gs) * gs : center.y;
    // Resolve fields. browserItem may be from monsters DB or our characters DB
    const raw = browserItem.raw || browserItem;
    const hp = parseInt(raw.hit_points || raw.hp) || 0;
    const ac = parseInt(raw.armor_class || raw.ac) || 10;
    const tokenImg = raw.token_image || raw.image_path || raw.image_url || "";
    const computedType = sourceType === "monster" ? "monster" : (raw.character_type || sourceType);
    const isPlayer = computedType === "pc" || computedType === "character" || computedType === "companion";
    const body = {
      source_id: raw.id,
      source_type: sourceType === "monster" ? "monster" : "character",
      name: raw.name,
      x: sx, y: sy,
      size: SIZE_MAP[raw.size] || 1,
      hp, max_hp: hp, ac,
      is_player: isPlayer,
      color: isPlayer ? "#4a9eff" : sourceType === "monster" ? "#ef4444" : "#c4a55a",
      token_image: tokenImg,
      layer: activeLayer || "tokens",
      visible_to_players: activeLayer !== "gm",
    };
    try {
      const res = await axios.post(`${API}/vtt-sessions/${activeSession.id}/tokens`, body, { withCredentials: true });
      setActiveSession(p => ({ ...p, tokens: [...(p.tokens || []), res.data] }));
      emitAddToken(res.data);
      toast.success(`${raw.name} placed`);
    } catch { toast.error("Failed to place token"); }
  };

  const placeAssetFromBrowser = async (assetKey, dropPos) => {
    if (!activeSession) return;
    const center = dropPos || (() => {
      const rect = canvasAreaRef.current?.getBoundingClientRect();
      if (!rect) return { x: 100, y: 100 };
      return {
        x: (rect.width / 2 - viewport.x) / viewport.scale,
        y: (rect.height / 2 - viewport.y) / viewport.scale,
      };
    })();
    const newAsset = {
      id: `a-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      asset_key: assetKey,
      x: center.x, y: center.y,
      size: activeSession?.grid_size || 50,
      rotation: 0,
      layer: activeLayer || "tokens",
    };
    const next = [...mapAssets, newAsset];
    setMapAssetsState(next);
    saveAssets(next);
  };

  const handleMapDrop = async (e) => {
    e.preventDefault();
    setIsDraggingMap(false);
    if (!activeSession) return;

    const rect = canvasAreaRef.current?.getBoundingClientRect();
    let dropPos = null;
    if (rect) {
      const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
      const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
      dropPos = { x: wx, y: wy };
    }

    // NEW: Token Browser drop
    const tbData = e.dataTransfer?.getData("application/x-natone-token");
    if (tbData) {
      try {
        const payload = JSON.parse(tbData);
        await placeTokenFromBrowser(payload, payload._src === "monster" ? "monster" : "character", dropPos);
      } catch { toast.error("Failed to place token"); }
      return;
    }
    // NEW: Asset Browser drop
    const abData = e.dataTransfer?.getData("application/x-natone-asset");
    if (abData) {
      try {
        const { assetKey } = JSON.parse(abData);
        await placeAssetFromBrowser(assetKey, dropPos);
      } catch { toast.error("Failed to place asset"); }
      return;
    }
    // NEW: Spell drop (drag-only placement — prevents accidental click-to-cast)
    const spellData = e.dataTransfer?.getData("application/x-natone-spell");
    if (spellData) {
      try {
        const spell = JSON.parse(spellData);
        if (dropPos) {
          addSpellEffect(spell, dropPos.x, dropPos.y, spellRotation || 0);
          toast.success(`${spell.name} placed`);
        }
      } catch { toast.error("Failed to place spell"); }
      setSpellDragPreview(null);
      return;
    }
    // Legacy: tokenEntity drop from old right sidebar
    const tokenEntityData = e.dataTransfer?.getData("tokenEntity");
    if (tokenEntityData) {
      try {
        const entity = JSON.parse(tokenEntityData);
        const type = entity._type;
        const rect = canvasAreaRef.current?.getBoundingClientRect();
        if (rect) {
          const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
          const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
          const gs = activeSession.grid_size || 50;
          const sx = snapToGrid ? Math.round(wx / gs) * gs : wx;
          const sy = snapToGrid ? Math.round(wy / gs) * gs : wy;
          const hp = parseInt(entity.hit_points) || 0;
          const ac = parseInt(entity.armor_class) || 10;
          const tokenImg = entity.token_image || entity.image_url || entity.image_path || "";
          const body = { source_id: entity.id, source_type: type, name: entity.name, x: sx, y: sy, size: SIZE_MAP[entity.size] || 1, hp, max_hp: hp, ac, is_player: type === "character", color: type === "character" ? "#4a9eff" : type === "npc" ? "#c4a55a" : "#ef4444", token_image: tokenImg, layer: activeLayer || "tokens", visible_to_players: activeLayer !== "gm" };
          const res = await axios.post(`${API}/vtt-sessions/${activeSession.id}/tokens`, body, { withCredentials: true });
          setActiveSession(p => ({ ...p, tokens: [...(p.tokens || []), res.data] }));
          emitAddToken(res.data);
          toast.success(`${entity.name} placed`);
        }
      } catch { toast.error("Failed to place token"); }
      return;
    }

    // File drop for map upload
    const file = e.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("grid_size", activeSession.grid_size || 50);
    try {
      toast.info("Uploading map...");
      const res = await axios.post(`${API}/vtt-sessions/${activeSession.id}/upload-map`, formData, { withCredentials: true });
      setActiveSession(p => ({ ...p, map_url: res.data.map_url, map_id: res.data.map_id }));
      toast.success("Map uploaded and applied!");
    } catch { toast.error("Failed to upload map"); }
  };

  const applyMapToSession = async (map) => {
    if (!activeSession) return;
    const mapUrl = map.storage_path || map.image_path || map.image_url || "";
    if (!mapUrl) { toast.error("Map has no image"); return; }
    try {
      // Save current tokens + fog + spells + assets keyed to current map
      const currentMapKey = activeSession.map_url || "_no_map";
      setMapStates(prev => ({ ...prev, [currentMapKey]: { tokens: activeSession.tokens || [], fog_areas: activeSession.fog_areas || [], spells: spellEffects || [], assets: mapAssets || [] } }));
      // Load saved state for the new map (if any)
      const saved = mapStates[mapUrl];
      const newTokens = saved?.tokens || [];
      const newFog = saved?.fog_areas || [];
      const newSpells = saved?.spells || [];
      const newAssets = saved?.assets || [];
      await axios.put(`${API}/vtt-sessions/${activeSession.id}`, { map_url: mapUrl, map_id: map.id, tokens: newTokens, fog_areas: newFog, spells: newSpells, assets: newAssets }, { withCredentials: true });
      setActiveSession(p => ({ ...p, map_url: mapUrl, map_id: map.id, tokens: newTokens, fog_areas: newFog, spells: newSpells, assets: newAssets }));
      setSpellEffects(newSpells);
      setMapAssetsState(newAssets);
      setSelectedSpellId(null);
      setSelectedAssetId(null);
      // Broadcast new spells + assets to players
      emitSpellsUpdate(newSpells);
      emitAssetsUpdate(newAssets);
      setMapPickerOpen(false);
      toast.success(`Map "${map.name}" applied`);
    } catch { toast.error("Failed to apply map"); }
  };

  // ==================== EQUIPMENT ====================
  useEffect(() => {
    if (selectedToken?.source_type === "character" && selectedToken?.source_id) {
      axios.get(`${API}/characters/${selectedToken.source_id}/equipment`, { withCredentials: true }).then(r => setCharEquipment(r.data)).catch(() => setCharEquipment([]));
    } else setCharEquipment([]);
  }, [selectedToken?.id]);

  const toggleEquip = async (itemId) => {
    try { const r = await axios.put(`${API}/items/${itemId}/equip`, {}, { withCredentials: true }); setCharEquipment(p => p.map(i => i.id === itemId ? { ...i, equipped: r.data.equipped } : i)); } catch {}
  };

  const launchEncounter = async (encounterId) => {
    if (!activeSession) return;
    try {
      const r = await axios.post(`${API}/vtt-sessions/${activeSession.id}/launch-encounter`, { encounter_id: encounterId }, { withCredentials: true });
      toast.success(r.data.message);
      await loadSession(activeSession.id);
      // Auto-populate initiative tracker with all tokens on the map
      const sess = activeSession;
      setTimeout(() => {
        setActiveSession(current => {
          if (!current) return current;
          const tokens = current.tokens || [];
          if (tokens.length > 0) {
            const initEntries = tokens.map(t => ({
              id: t.id, name: t.name,
              initiative: Math.floor(Math.random() * 20) + 1, // auto-roll d20
              isActive: false,
            })).sort((a, b) => b.initiative - a.initiative);
            if (initEntries.length > 0) initEntries[0].isActive = true;
            setInitiativeOrder(initEntries);
            setShowInitTracker(true);
            setCombatActive(true);
            setSessionActive(true);
          }
          return current;
        });
      }, 500);
    } catch { toast.error("Failed to launch"); }
  };

  const generateTokenArt = async (type, id) => {
    try { toast.info("Generating token..."); await axios.post(`${API}/entities/${type}/${id}/generate-token`, {}, { withCredentials: true }); toast.success("Token generation started"); } catch { toast.error("Failed"); }
  };

  const getMapUrl = () => {
    if (!activeSession?.map_url) return null;
    const u = activeSession.map_url;
    if (u === "None" || u === "null") return null;
    if (u.startsWith("http")) return u;
    return `${FILE_URL}/${u}`;
  };
  const isInternalMap = () => {
    const u = activeSession?.map_url;
    return u && !u.startsWith("http") && u !== "None" && u !== "null";
  };

  const filterEntities = (list) => (list || []).filter(e => e.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const gs = activeSession?.grid_size || 50;

  // ==================== RENDER ====================
  if (loading) return <div className="h-screen bg-[#121212] flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="h-screen flex flex-col bg-[#1a1a1a] overflow-hidden select-none" data-testid="vtt-page">
      {/* TOP BAR */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#222] border-b border-[#333] flex-shrink-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/campaign/${campaignId}/chapters`)} className="text-xs text-[#888] hover:text-white transition-colors" data-testid="vtt-back">&larr; Back to Campaign</button>
          <div className="w-px h-4 bg-[#444]" />
          <h2 className="text-sm font-serif text-white font-medium truncate max-w-[200px]">{activeSession?.name || "Virtual Tabletop"}</h2>
          {sessions.length > 1 && (
            <Select value={activeSession?.id || ""} onValueChange={(id) => loadSession(id)}>
              <SelectTrigger className="w-[160px] h-7 text-xs bg-[#333] border-[#444] rounded-sm text-white" data-testid="vtt-session-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#333] border-[#444]">{sessions.map(s => <SelectItem key={s.id} value={s.id} className="text-xs text-white">{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#888] hover:text-white" onClick={() => setCreateDialogOpen(true)} data-testid="create-vtt-session"><Plus className="w-3 h-3 mr-1" /> New</Button>
          {activeSession && <Button size="sm" variant="ghost" className="h-7 text-xs text-[#888] hover:text-white" onClick={() => setMapPickerOpen(true)} data-testid="open-map-picker"><Map className="w-3 h-3 mr-1" /> Maps</Button>}
        </div>
        <div className="flex items-center gap-1">
          {["characters", "npcs", "items", "handouts"].map(panel => (
            <Button key={panel} size="sm" variant={infoPanel === panel ? "default" : "ghost"} className={`h-7 text-xs ${infoPanel !== panel ? "text-[#888] hover:text-white" : ""}`} onClick={() => setInfoPanel(infoPanel === panel ? null : panel)} data-testid={`panel-${panel}`}>
              {panel === "characters" ? <><UserCircle2 className="w-3.5 h-3.5 mr-1" />Players</> : panel === "npcs" ? <><Users className="w-3.5 h-3.5 mr-1" />NPCs</> : panel === "items" ? <><Package className="w-3.5 h-3.5 mr-1" />Items</> : <><FileText className="w-3.5 h-3.5 mr-1" />Handouts</>}
            </Button>
          ))}
          <div className="w-px h-5 bg-[#444] mx-1" />
          <Button size="sm" variant={showInitTracker ? "default" : "ghost"} className={`h-7 text-xs ${!showInitTracker ? "text-[#888] hover:text-white" : ""}`}
            onClick={() => {
              if (!showInitTracker && initiativeOrder.length === 0) {
                // New Combat: add all current tokens to initiative
                const tokens = activeSession?.tokens || [];
                if (tokens.length > 0) {
                  const entries = tokens.map(t => ({ id: t.id, name: t.name, initiative: Math.floor(Math.random() * 20) + 1, isActive: false })).sort((a, b) => b.initiative - a.initiative);
                  entries[0].isActive = true;
                  setInitiativeOrder(entries);
                  setCombatActive(true);
                }
                setShowInitTracker(true);
              } else {
                setShowInitTracker(!showInitTracker);
              }
            }}
            data-testid="combat-tracker-btn"
          >
            <Swords className="w-3.5 h-3.5 mr-1" />
            {combatActive ? `Combat (${initiativeOrder.length})` : "Initiative"}
          </Button>
          {/* Phase 3 — Vision Mode controls (DM-only). Top-level Roll20-style
              selector: Off / Token Vision (dynamic LOS from each token) / Fog
              of War (DM-painted manual fog). When Token Vision is active a
              secondary select controls persistence (memory vs live-only). */}
          {activeSession && (
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[#444]" data-testid="lighting-controls">
              <Lightbulb className="w-3.5 h-3.5 text-[#888]" />
              <Select
                value={lightingEnabled ? "token_vision" : "fog_of_war"}
                onValueChange={(v) => updateVisionMode(v === "token_vision")}
              >
                <SelectTrigger
                  className="h-7 w-[140px] text-xs bg-transparent border-[#444] text-[#ccc]"
                  data-testid="vision-mode-select"
                  title={walls.length === 0 ? "Tip: draw walls first for Token Vision to take effect" : "Switch between Token Vision and Fog of War"}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="token_vision" data-testid="vision-mode-token">Token Vision</SelectItem>
                  <SelectItem value="fog_of_war" data-testid="vision-mode-fog">Fog of War</SelectItem>
                </SelectContent>
              </Select>
              {lightingEnabled && (
                <Select value={lightingMode} onValueChange={updateLightingMode}>
                  <SelectTrigger className="h-7 w-[120px] text-xs bg-transparent border-[#444] text-[#ccc]" data-testid="lighting-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="persistent">Persistent Fog</SelectItem>
                    <SelectItem value="los">True LOS</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {walls.length > 0 && (
                <span className="text-[10px] text-[#666] tabular-nums" title="Walls placed">{walls.length} walls</span>
              )}
              {walls.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[10px] text-[#888] hover:text-red-400 px-1"
                  onClick={() => { if (window.confirm("Delete all walls and doors?")) { clearAllWalls(); setRevealedPolygons([]); } }}
                  title="Clear all walls"
                  data-testid="clear-walls"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MAIN AREA */}
      <div className="flex flex-1 overflow-hidden">

        {/* CANVAS AREA */}
        <div ref={canvasAreaRef} className={`flex-1 relative overflow-hidden bg-[#1a1a2e] ${isDraggingMap ? "ring-2 ring-primary ring-inset" : ""}`} style={{ cursor: isPanning ? "grabbing" : dragToken ? "grabbing" : (activeTool === "fog_add" || activeTool === "fog_remove" || activeTool === "fog_rect") ? "crosshair" : "default", touchAction: "none" }}
          onPointerDown={handleCanvasPointerDown} onPointerMove={handleCanvasPointerMove} onPointerUp={handleCanvasPointerUp} onPointerLeave={handleCanvasPointerUp}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
          onContextMenu={e => {
            e.preventDefault();
            // Right-click while drawing a wall/door/window — detach the chain so the
            // DM can start a fresh segment elsewhere without committing the last line.
            if ((activeTool === "wall_add" || activeTool === "door_add" || activeTool === "window_add") && wallStart) {
              setWallStart(null);
              setWallPreview(null);
            }
          }} onDragOver={e => {
            e.preventDefault();
            setIsDraggingMap(true);
            // Update spell preview position while dragging (works only if drag started with spell payload)
            if (spellDragPreview) {
              const rect = canvasAreaRef.current?.getBoundingClientRect();
              if (rect) {
                const wx = (e.clientX - rect.left - viewport.x) / viewport.scale;
                const wy = (e.clientY - rect.top - viewport.y) / viewport.scale;
                setSpellDragPreview(prev => prev ? { ...prev, x: wx, y: wy } : prev);
              }
            }
          }} onDragLeave={() => { setIsDraggingMap(false); setSpellDragPreview(null); }} onDrop={handleMapDrop}
          data-testid="vtt-canvas-area"
        >
          {!activeSession ? (
            <div className="absolute inset-0 flex items-center justify-center"><div className="text-center"><MapPin className="w-12 h-12 text-[#444] mx-auto mb-3" /><h3 className="text-lg font-serif text-white mb-2">No Session Active</h3><p className="text-[#777] text-sm mb-4">Create a session to start</p><Button onClick={() => setCreateDialogOpen(true)} className="bg-red-600 hover:bg-red-700 text-white rounded-sm"><Plus className="w-4 h-4 mr-2" />Create Session</Button></div></div>
          ) : (
            /* Viewport transform container */
            <div style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`, transformOrigin: "0 0", position: "absolute", top: 0, left: 0 }} data-testid="vtt-viewport">
              {/* Map image */}
              {getMapUrl() && <img src={getMapUrl()} alt="map" className="absolute top-0 left-0 pointer-events-none" style={{ imageRendering: "auto", maxWidth: "none" }} draggable={false} data-testid="vtt-map-image" onError={(e) => { console.error("Map load failed:", e.target.src); e.target.style.display = "none"; }} onLoad={(e) => { console.log("Map loaded:", e.target.naturalWidth, "x", e.target.naturalHeight); }} />}

              {/* Grid overlay */}
              {activeSession.grid_enabled && (
                <div className="absolute top-0 left-0 pointer-events-none" style={{ width: 3000, height: 3000, backgroundSize: `${gs}px ${gs}px`, backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)" }} data-testid="vtt-grid" />
              )}

              {/* Fog — rendered above tokens so they hide beneath it. DM opacity is adjustable so DM can peek under fog. */}
              {/* Phase 3: when lighting is enabled, vision sources are clipped to LOS polygons that respect walls + light sources */}
              {(() => {
                const fogAreas = (activeSession.fog_areas || []).filter(f => !f.revealed);
                const tokensList = activeSession.tokens || [];
                // Vision sources from tokens — supports both new (vision_bright/dim/dark) and legacy (vision_radius) fields
                const tokenSources = lightingEnabled
                  ? tokenVisionSources(tokensList, gs / 5)
                  : tokensList.filter(t => (t.vision_radius || 0) > 0).map(t => {
                      const tsz = (t.size || 1) * gs;
                      return { id: `tok-${t.id}`, x: t.x + tsz / 2, y: t.y + tsz / 2, radius: (t.vision_radius / 5) * gs };
                    });
                // Light-emitter assets (torch/lantern/campfire have non-zero light_bright)
                const lightSources = lightingEnabled ? assetLightSources(mapAssets, gs / 5) : [];
                const allSources = [...tokenSources, ...lightSources];

                // LOS polygon paths — only computed when lighting is enabled (otherwise fall back to simple radial gradient)
                const polyPaths = lightingEnabled
                  ? allSources.map(s => polygonToPath(computeVisibilityPolygon(s.x, s.y, s.radius, walls)))
                  : [];

                return (
                  <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible", zIndex: 50 }} data-testid="vtt-fog-layer">
                    <defs>
                      <mask id="dm-fog-mask">
                        <rect x="0" y="0" width="4000" height="4000" fill="black" />
                        {fogAreas.map(f => (
                          f.cx !== undefined
                            ? <circle key={f.id} cx={f.cx} cy={f.cy} r={f.r} fill="white" />
                            : <rect key={f.id} x={f.x} y={f.y} width={f.width} height={f.height} fill="white" />
                        ))}
                        {/* Persistent reveal — accumulated visibility polygons from prior frames */}
                        {lightingEnabled && lightingMode === "persistent" && revealedPolygons.map((p, i) => (
                          <path key={`rev-${i}`} d={p.path} fill="white" />
                        ))}
                        {lightingEnabled ? (
                          // LOS-clipped polygons (no soft fade — wall edges should be sharp)
                          polyPaths.map((d, i) => (
                            <path key={`los-${allSources[i].id}`} d={d} fill="white" data-testid={`los-poly-${allSources[i].id}`} />
                          ))
                        ) : (
                          // Legacy: simple radial-gradient circles
                          tokenSources.map(s => {
                            const rId = `dm-vis-fade-${s.id}`;
                            return (
                              <g key={s.id}>
                                <defs>
                                  <radialGradient id={rId} cx="50%" cy="50%" r="50%">
                                    <stop offset="70%" stopColor="black" stopOpacity="1" />
                                    <stop offset="100%" stopColor="black" stopOpacity="0" />
                                  </radialGradient>
                                </defs>
                                <circle cx={s.x} cy={s.y} r={s.radius} fill={`url(#${rId})`} />
                              </g>
                            );
                          })
                        )}
                      </mask>
                    </defs>
                    {(fogAreas.length > 0 || (lightingEnabled && walls.length > 0)) && (
                      <rect x="0" y="0" width="4000" height="4000" fill={`rgba(0,0,0,${dmFogOpacity})`} mask="url(#dm-fog-mask)" />
                    )}
                    {/* Vision indicator for DM (faint amber ring) */}
                    {!lightingEnabled && tokenSources.map(s => (
                      <circle key={`vi-${s.id}`} cx={s.x} cy={s.y} r={s.radius} fill="none" stroke="#fbbf24" strokeWidth="1" strokeOpacity="0.35" strokeDasharray="6 6" data-testid={`vision-ring-${s.id}`} />
                    ))}
                    {/* Fog rectangle preview while dragging */}
                    {fogRectPreview && (
                      <rect x={fogRectPreview.x} y={fogRectPreview.y} width={fogRectPreview.width} height={fogRectPreview.height} fill={`rgba(0,0,0,${Math.min(0.5, dmFogOpacity)})`} stroke="#fbbf24" strokeWidth="2" strokeDasharray="6 4" />
                    )}
                  </svg>
                );
              })()}

              {/* Walls + Doors + Windows layer (DM-visible) */}
              {walls.length > 0 && (
                <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible", zIndex: 51 }} data-testid="vtt-walls-layer">
                  {walls.map(w => {
                    const isDoor = w.kind === "door";
                    const isWindow = w.kind === "window";
                    let colour = "#a855f7";  // wall (purple)
                    let dash = "";
                    let width = 4;
                    if (isDoor) {
                      colour = w.open ? "#22c55e" : "#fbbf24"; // green when open, amber when closed
                      dash = w.open ? "8 4" : "";
                      width = 5;
                    } else if (isWindow) {
                      // Window: dashed cyan when open (light passes), solid thicker cyan when shuttered (light blocks)
                      colour = "#22d3ee";
                      dash = w.open === false ? "" : "3 3";
                      width = w.open === false ? 4 : 3;
                    }
                    return (
                      <line
                        key={w.id}
                        x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2}
                        stroke={colour}
                        strokeWidth={width}
                        strokeOpacity={lightingEnabled ? 0.7 : 0.95}
                        strokeLinecap="round"
                        strokeDasharray={dash}
                        data-testid={`wall-${w.id}`}
                        data-kind={w.kind || "wall"}
                        data-open={w.open ? "yes" : "no"}
                      />
                    );
                  })}
                </svg>
              )}

              {/* Wall drawing preview (live line from anchor to cursor) */}
              {wallStart && wallPreview && (activeTool === "wall_add" || activeTool === "door_add" || activeTool === "window_add") && (
                <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible", zIndex: 52 }}>
                  <line
                    x1={wallStart.x} y1={wallStart.y}
                    x2={wallPreview.x} y2={wallPreview.y}
                    stroke={activeTool === "door_add" ? "#fbbf24" : activeTool === "window_add" ? "#22d3ee" : "#a855f7"}
                    strokeWidth="3"
                    strokeOpacity="0.7"
                    strokeDasharray="6 4"
                  />
                  <circle cx={wallStart.x} cy={wallStart.y} r="4" fill={activeTool === "door_add" ? "#fbbf24" : activeTool === "window_add" ? "#22d3ee" : "#a855f7"} />
                </svg>
              )}


              {/* Map Asset Layer (between background and tokens) */}
              {mapAssets.map(a => {
                const def = findAsset(a.asset_key);
                if (!def) return null;
                const layerLocked = (a.layer || "tokens") !== activeLayer;
                const isSelected = selectedAssetId === a.id;
                const isMultiSel = selectedAssetIds.includes(a.id);
                const sz = a.size || 40;
                const isOnGM = a.layer === "gm";
                return (
                  <div
                    key={a.id}
                    className={`absolute group ${layerLocked ? "" : "cursor-pointer"}`}
                    style={{
                      left: a.x - sz / 2,
                      top: a.y - sz / 2,
                      width: sz,
                      height: sz,
                      zIndex: a.layer === "map" ? 5 : 8,
                      transform: `rotate(${a.rotation || 0}deg)`,
                      pointerEvents: layerLocked || placingAsset ? "none" : "auto",
                      opacity: isOnGM ? 0.55 : 1,
                      filter: (isSelected || isMultiSel) ? "drop-shadow(0 0 6px #fbbf24)" : isOnGM ? "grayscale(0.4)" : undefined,
                    }}
                    onPointerDown={(e) => {
                      if (layerLocked || placingAsset) return;
                      e.stopPropagation();
                      const rect = canvasAreaRef.current?.getBoundingClientRect();
                      if (!rect) return;
                      const mapX = (e.clientX - rect.left - viewport.x) / viewport.scale;
                      const mapY = (e.clientY - rect.top - viewport.y) / viewport.scale;
                      // Group-drag: clicking a multi-selected asset moves the whole bundle
                      if (selectedAssetIds.length > 1 && selectedAssetIds.includes(a.id)) {
                        const origPositions = {};
                        mapAssets.forEach(x => { if (selectedAssetIds.includes(x.id)) origPositions[x.id] = { x: x.x, y: x.y }; });
                        assetDragRef.current = {
                          active: true,
                          id: a.id,
                          offsetX: mapX - a.x,
                          offsetY: mapY - a.y,
                          groupIds: [...selectedAssetIds],
                          anchorOrig: { x: a.x, y: a.y },
                          origPositions,
                        };
                        return;
                      }
                      setSelectedAssetId(a.id);
                      setSelectedAssetIds([]);
                      setSelectedSpellId(null);
                      setSelectedToken(null);
                      setSelectedTokenIds([]);
                      assetDragRef.current = { active: true, id: a.id, offsetX: mapX - a.x, offsetY: mapY - a.y };
                    }}
                    title={def.name}
                    data-testid={`map-asset-${a.id}`}
                  >
                    <img
                      src={def.url}
                      alt={def.name}
                      draggable={false}
                      className="w-full h-full"
                      style={{ userSelect: "none", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))", pointerEvents: "none" }}
                    />
                    {(isSelected || isMultiSel) && <div className="absolute inset-0 ring-2 ring-amber-400 rounded-sm pointer-events-none" />}
                  </div>
                );
              })}

              {/* Tokens */}
              {(activeSession.tokens || []).map(token => {
                const sz = (token.size || 1) * gs;
                const isSelected = selectedToken?.id === token.id || selectedTokenIds.includes(token.id);
                const hpPct = token.max_hp > 0 ? token.hp / token.max_hp : 1;
                const conditions = token.conditions || [];
                const isDead = token.is_dead;
                const isGMLayer = (token.layer === "gm");
                const isHidden = !token.visible_to_players;
                const isDragging = dragToken === token.id || (dragToken && selectedTokenIds.includes(token.id) && selectedTokenIds.includes(dragToken));
                const tokenLayer = token.layer || "tokens";
                const layerLocked = tokenLayer !== activeLayer; // can't edit tokens on other layers
                return (
                  <div key={token.id} className={`${isDragging ? "" : "vtt-token"} absolute group ${tokenAnim[token.id] ? `vtt-anim-${tokenAnim[token.id]}` : ""} ${isDead && !tokenAnim[token.id] ? "vtt-token-dead" : ""} ${layerLocked ? "" : "cursor-pointer"}`} style={{ left: token.x, top: token.y, width: sz, height: sz, zIndex: dragToken === token.id ? 100 : 10, opacity: isDead ? 0.55 : isGMLayer ? 0.5 : isHidden ? 0.6 : 1, filter: isGMLayer ? "grayscale(0.3)" : undefined, pointerEvents: layerLocked ? "none" : "auto" }}
                    onPointerDown={(e) => {
                      if (layerLocked) return;
                      e.stopPropagation();
                      setContextMenu(null);
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {
                        setSelectedTokenIds(prev => prev.includes(token.id) ? prev.filter(id => id !== token.id) : [...prev, token.id]);
                        setSelectedToken(token);
                      } else {
                        if (!selectedTokenIds.includes(token.id)) setSelectedTokenIds([]);
                        setSelectedToken(token);
                      }
                      if (activeTool === "select" || activeTool === "move") { setDragToken(token.id); }
                    }}
                    onDoubleClick={(e) => { if (layerLocked) return; e.stopPropagation(); handleTokenDoubleClick(token); }}
                    onContextMenu={(e) => { if (layerLocked) { e.preventDefault(); return; } handleTokenContextMenu(e, token); }}
                    data-testid={`vtt-token-${token.id}`}
                  >
                    {/* Condition rings */}
                    {conditions.length > 0 && (
                      <div className="absolute -inset-2 pointer-events-none" style={{ zIndex: -1 }}>
                        <div className="w-full h-full rounded-full" style={{ border: `3px solid ${getConditionColor(conditions[0])}`, boxShadow: `0 0 8px ${getConditionColor(conditions[0])}40` }} />
                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-0.5">
                          {conditions.map((c, i) => (
                            <span key={i} className="text-[7px] px-1 py-px rounded-sm whitespace-nowrap font-bold" style={{ backgroundColor: getConditionColor(c), color: "#fff" }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {isSelected && <div className="absolute -inset-1 rounded-full border-2 border-yellow-400 animate-pulse" />}
                    {/* Token circle or image */}
                    <div className="w-full h-full rounded-full border-2 border-white/60 flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: token.color || "#c4a55a" }}>
                      {(() => { const imgSrc = resolveImageUrl(token.token_image); return imgSrc ? (
                        <img src={imgSrc} alt="" className="w-full h-full object-cover rounded-full" draggable={false} />
                      ) : (
                        <><div className="absolute inset-0 bg-black/20 rounded-full" /><span className="relative text-white font-bold text-center leading-tight drop-shadow-lg" style={{ fontSize: Math.max(8, sz / 4) }}>{token.name?.substring(0, Math.max(3, Math.floor(sz / 8))) || "?"}</span></>
                      ); })()}
                    </div>
                    {/* Dead X overlay */}
                    {isDead && (
                      <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 100 100">
                        <line x1="15" y1="15" x2="85" y2="85" stroke="#ef4444" strokeWidth="8" strokeLinecap="round" />
                        <line x1="85" y1="15" x2="15" y2="85" stroke="#ef4444" strokeWidth="8" strokeLinecap="round" />
                      </svg>
                    )}
                    {token.max_hp > 0 && !isDead && (
                      <div className="absolute bottom-0.5 left-1 right-1 h-1.5 bg-black/60 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${hpPct * 100}%`, backgroundColor: hpPct > 0.5 ? "#22c55e" : hpPct > 0.25 ? "#eab308" : "#ef4444" }} />
                      </div>
                    )}
                    {/* Resize handle */}
                    {isSelected && (
                      <div className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-yellow-400 border border-yellow-600 rounded-sm cursor-se-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                        onPointerDown={(e) => { e.stopPropagation(); setResizeToken({ id: token.id }); }}
                        data-testid={`resize-handle-${token.id}`}
                      />
                    )}
                    {/* Hover quick-stats tooltip — name, HP, AC, conditions */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 -translate-y-full -top-2 bg-[#0f0d0a]/95 border border-amber-500/40 rounded-sm px-2 py-1.5 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-30"
                      data-testid={`token-hover-${token.id}`}
                      style={{ minWidth: 110 }}
                    >
                      <div className="text-[11px] font-bold leading-tight">{token.name}{isDead ? " (Dead)" : ""}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {token.max_hp > 0 && (
                          <span className="flex items-center gap-0.5 text-[10px]">
                            <Heart className="w-2.5 h-2.5" style={{ color: hpPct > 0.5 ? "#22c55e" : hpPct > 0.25 ? "#eab308" : "#ef4444" }} />
                            <span style={{ color: hpPct > 0.5 ? "#22c55e" : hpPct > 0.25 ? "#eab308" : "#ef4444" }}>{token.hp}/{token.max_hp}</span>
                          </span>
                        )}
                        {token.ac && (
                          <span className="flex items-center gap-0.5 text-[10px]">
                            <Shield className="w-2.5 h-2.5 text-blue-400" />
                            <span className="text-blue-300">{token.ac}</span>
                          </span>
                        )}
                        {(token.size && token.size !== 1) && (
                          <span className="text-[9px] text-[#888]">{token.size}sq</span>
                        )}
                      </div>
                      {conditions.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1 max-w-[180px]">
                          {conditions.slice(0, 4).map((c, i) => (
                            <span key={i} className="text-[8px] px-1 rounded-sm" style={{ backgroundColor: `${getConditionColor(c)}30`, color: getConditionColor(c), border: `1px solid ${getConditionColor(c)}60` }}>{c}</span>
                          ))}
                          {conditions.length > 4 && <span className="text-[8px] text-[#888]">+{conditions.length - 4}</span>}
                        </div>
                      )}
                      {isHidden && <div className="text-[9px] text-amber-400 mt-0.5">Hidden from players</div>}
                    </div>
                  </div>
                );
              })}

              {/* SVG overlay for measurement shapes & pings */}
              <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: 4000, height: 4000, overflow: "visible" }}>
                {/* Measurement shapes */}
                {shapes.map(s => <MeasureShape key={s.id} shape={s} gs={gs} />)}
                {/* Draw preview */}
                {drawPreview && <MeasureShape shape={drawPreview} gs={gs} preview />}
                {/* Drag-selection box */}
                {selectionBox && (
                  <rect
                    x={Math.min(selectionBox.startX, selectionBox.endX)}
                    y={Math.min(selectionBox.startY, selectionBox.endY)}
                    width={Math.abs(selectionBox.endX - selectionBox.startX)}
                    height={Math.abs(selectionBox.endY - selectionBox.startY)}
                    fill="rgba(251,191,36,0.08)"
                    stroke="#fbbf24"
                    strokeWidth="1"
                    strokeDasharray="4 3"
                  />
                )}
                {/* Pings — large pulsing circle so DM/players can see exactly where someone points */}
                {pings.map(p => (
                  <g key={p.id} style={{ pointerEvents: "none" }}>
                    {/* Outer expanding ring */}
                    <circle cx={p.x} cy={p.y} r={gs * 0.4} fill="none" stroke="#fbbf24" strokeWidth="4" opacity="0.95">
                      <animate attributeName="r" from={gs * 0.3} to={gs * 1.6} dur="1.4s" repeatCount="3" />
                      <animate attributeName="opacity" from="0.95" to="0" dur="1.4s" repeatCount="3" />
                    </circle>
                    {/* Static centre disc */}
                    <circle cx={p.x} cy={p.y} r={gs * 0.22} fill="#fbbf24" opacity="0.9">
                      <animate attributeName="opacity" values="0.9;0.4;0.9" dur="0.6s" repeatCount="6" />
                    </circle>
                    {/* Bright pinpoint */}
                    <circle cx={p.x} cy={p.y} r={gs * 0.08} fill="#fff" />
                    {/* Player name label */}
                    {p.name && (
                      <g transform={`translate(${p.x} ${p.y - gs * 0.55})`}>
                        <rect x={-((p.name.length * 5.5) / 2) - 6} y={-9} width={p.name.length * 5.5 + 12} height={16} rx="4" fill="#1f1b16" stroke="#fbbf24" strokeWidth="1.2" opacity="0.95" />
                        <text x={0} y={2} textAnchor="middle" fontSize={11} fontWeight="600" fill="#fbbf24">{p.name}</text>
                      </g>
                    )}
                  </g>
                ))}
                {/* Spell effects */}
                {spellEffects.map(se => {
                  const svg = getSpellSVG({ spell: se.spell, x: se.x, y: se.y, rotation: se.rotation, gs });
                  if (!svg) return null;
                  const schoolColor = SPELL_SCHOOLS[se.spell.school]?.color || "#ef4444";
                  const isSelected = selectedSpellId === se.id;
                  const commonShapeProps = { fill: svg.fill, stroke: isSelected ? "#fbbf24" : svg.stroke, strokeWidth: isSelected ? 3 : 2, strokeOpacity: svg.strokeOpacity, strokeDasharray: isSelected ? "" : "6 3" };
                  return (
                    <g
                      key={se.id}
                      style={{ cursor: spellDragRef.current.active && spellDragRef.current.id === se.id ? "grabbing" : "grab", pointerEvents: castingSpell ? "none" : "all" }}
                      onMouseDown={(e) => {
                        if (castingSpell) return;
                        e.stopPropagation();
                        if (selectedSpellId !== se.id) {
                          setSelectedSpellId(se.id);
                          const canRotate = se.spell.shape === "cone" || se.spell.shape === "line";
                          toast.info(canRotate ? "Drag to move • Scroll or [ ] to rotate • Del to remove" : "Drag to move • Del to remove", { duration: 2000 });
                        }
                        const rect = canvasAreaRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        const mapX = (e.clientX - rect.left - viewport.x) / viewport.scale;
                        const mapY = (e.clientY - rect.top - viewport.y) / viewport.scale;
                        spellDragRef.current = { active: true, id: se.id, offsetX: mapX - se.x, offsetY: mapY - se.y };
                      }}
                    >
                      {svg.type === "circle" && <circle cx={svg.cx} cy={svg.cy} r={svg.r} {...commonShapeProps} />}
                      {svg.type === "rect" && <rect x={svg.x} y={svg.y} width={svg.width} height={svg.height} {...commonShapeProps} />}
                      {svg.type === "path" && <path d={svg.d} {...commonShapeProps} />}
                      {svg.type === "polygon" && <polygon points={svg.points} {...commonShapeProps} />}
                      <text x={se.x} y={se.y - 8} fill={isSelected ? "#fbbf24" : schoolColor} fontSize="11" fontWeight="bold" textAnchor="middle" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)", pointerEvents: "none" }}>{se.spell.name}</text>
                    </g>
                  );
                })}
                {/* Drag-preview ghost (only while user is dragging a spell from the sidebar) */}
                {spellDragPreview && spellDragPreview.x > -9000 && (() => {
                  const previewSvg = getSpellSVG({ spell: spellDragPreview.spell, x: spellDragPreview.x, y: spellDragPreview.y, rotation: spellRotation || 0, gs });
                  if (!previewSvg) return null;
                  const ghostColor = SPELL_SCHOOLS[spellDragPreview.spell.school]?.color || "#fbbf24";
                  const ghostProps = { fill: previewSvg.fill, fillOpacity: 0.25, stroke: ghostColor, strokeWidth: 2, strokeOpacity: 0.9, strokeDasharray: "4 4", pointerEvents: "none" };
                  return (
                    <g style={{ pointerEvents: "none" }} data-testid="spell-drag-preview">
                      {previewSvg.type === "circle" && <circle cx={previewSvg.cx} cy={previewSvg.cy} r={previewSvg.r} {...ghostProps} />}
                      {previewSvg.type === "rect" && <rect x={previewSvg.x} y={previewSvg.y} width={previewSvg.width} height={previewSvg.height} {...ghostProps} />}
                      {previewSvg.type === "path" && <path d={previewSvg.d} {...ghostProps} />}
                      {previewSvg.type === "polygon" && <polygon points={previewSvg.points} {...ghostProps} />}
                      <text x={spellDragPreview.x} y={spellDragPreview.y - 10} fill={ghostColor} fontSize="11" fontWeight="bold" textAnchor="middle" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)", pointerEvents: "none" }}>{spellDragPreview.spell.name}</text>
                    </g>
                  );
                })()}
              </svg>
            </div>
          )}

          {/* Toolbar */}
          {activeSession && <VTTToolbar activeTool={activeTool} setActiveTool={setActiveTool} activeLayer={activeLayer} setActiveLayer={setActiveLayer} gridSize={gs} setGridSize={updateGridSize} measurePersistent={measurePersistent} setMeasurePersistent={setMeasurePersistent} isDM={true} fogBrushSize={fogBrushSize} setFogBrushSize={setFogBrushSize}
            tokenBrowserOpen={tokenBrowserOpen}
            assetBrowserOpen={assetBrowserOpen}
            encounterOpen={showInitTracker}
            onOpenTokenBrowser={() => { setTokenBrowserOpen(o => !o); if (!tokenBrowserOpen) { setAssetBrowserOpen(false); setSpellBrowserOpen(false); } }}
            onOpenAssetBrowser={() => { setAssetBrowserOpen(o => !o); if (!assetBrowserOpen) { setTokenBrowserOpen(false); setSpellBrowserOpen(false); } }}
            onOpenSpellBrowser={() => { setSpellBrowserOpen(o => !o); if (!spellBrowserOpen) { setTokenBrowserOpen(false); setAssetBrowserOpen(false); } }}
            spellBrowserOpen={spellBrowserOpen}
            onToggleEncounter={() => { setShowInitTracker(s => !s); }}
            onClearFog={() => {
            if (!activeSession) return;
            axios.put(`${API}/vtt-sessions/${activeSession.id}/fog`, { fog_areas: [] }, { withCredentials: true })
              .then(() => { setActiveSession(p => ({ ...p, fog_areas: [] })); emitFogUpdate([]); toast.success("Fog cleared"); })
              .catch(() => toast.error("Failed to clear fog"));
          }} />}

          {/* TOKEN BROWSER (Phase 1 redesign) */}
          {activeSession && (
            <TokenBrowserPanel
              open={tokenBrowserOpen}
              onClose={() => setTokenBrowserOpen(false)}
              campaignId={campaignId}
              characters={(entities.characters || []).filter(c => (c.character_type || "pc") === "pc")}
              npcs={entities.npcs || []}
              creatures={entities.creatures || []}
              companions={(entities.characters || []).filter(c => (c.character_type || "") === "companion")}
              summons={(entities.characters || []).filter(c => (c.character_type || "") === "summon")}
              onPlaceToken={(entity, sourceType) => placeTokenFromBrowser(entity, sourceType)}
            />
          )}
          {/* ASSET BROWSER (Phase 1 redesign) */}
          {activeSession && (
            <AssetBrowserPanel
              open={assetBrowserOpen}
              onClose={() => setAssetBrowserOpen(false)}
              campaignId={campaignId}
              onPlaceAsset={(assetKey) => placeAssetFromBrowser(assetKey)}
            />
          )}
          {/* SPELL BROWSER (Phase 1 redesign) */}
          {activeSession && (
            <SpellBrowserPanel
              open={spellBrowserOpen}
              onClose={() => setSpellBrowserOpen(false)}
              castingSpell={castingSpell}
              setCastingSpell={setCastingSpell}
              spellEffects={spellEffects}
              removeSpellEffect={removeSpellEffect}
              clearSpellEffects={clearSpellEffects}
              setSelectedSpellId={setSelectedSpellId}
              setSpellRotation={setSpellRotation}
              onSpellDragStart={(spell) => setSpellDragPreview({ spell, x: -9999, y: -9999 })}
              onSpellDragEnd={() => setSpellDragPreview(null)}
            />
          )}

          {/* ASSET SIZE PANEL — appears for the currently selected asset */}
          {activeSession && selectedAssetId && (() => {
            const a = (mapAssets || []).find(x => x.id === selectedAssetId);
            if (!a) return null;
            const rect = canvasAreaRef.current?.getBoundingClientRect();
            if (!rect) return null;
            const sx = rect.left + viewport.x + (a.x + (a.size || activeSession.grid_size || 50)) * viewport.scale;
            const sy = rect.top + viewport.y + a.y * viewport.scale;
            return (
              <VTTAssetSizePanel
                asset={a}
                gridSize={activeSession.grid_size || 50}
                onChangeSize={(size) => setMapAssets(p => p.map(x => x.id === selectedAssetId ? { ...x, size } : x))}
                onChangeRotation={(rotation) => setMapAssets(p => p.map(x => x.id === selectedAssetId ? { ...x, rotation } : x))}
                onClose={() => setSelectedAssetId(null)}
                onDelete={() => { removeMapAsset(selectedAssetId); setSelectedAssetId(null); }}
                screen={{ x: sx, y: sy }}
              />
            );
          })()}

          {/* Drop overlay */}
          {isDraggingMap && <div className="absolute inset-0 z-30 flex items-center justify-center bg-primary/10 backdrop-blur-sm pointer-events-none"><div className="text-center"><Upload className="w-12 h-12 text-primary mx-auto mb-2 animate-bounce" /><p className="text-lg font-serif text-primary">Drop map here</p></div></div>}

          {/* RIGHT-CLICK CONTEXT MENU */}
          <VTTContextMenu
            menu={contextMenu}
            onClose={() => setContextMenu(null)}
            onAttackAnim={(id) => triggerTokenAnim(id, "attack", 500)}
            onToggleDead={toggleDead}
            onOpenConditions={(token) => { handleTokenDoubleClick(token); setConditionPickerOpen(true); }}
            onToggleCondition={toggleCondition}
            onChangeLayer={changeTokenLayer}
            onToggleVisible={setTokenVisibility}
            onRemove={removeToken}
          />

          {/* Clear shapes button */}
          {shapes.length > 0 && <button onClick={clearShapes} className="absolute top-3 right-12 z-20 bg-[#222]/90 border border-[#444] rounded px-2 py-1 text-[10px] text-[#888] hover:text-white" data-testid="clear-measurements">Clear Shapes ({shapes.length})</button>}

          {/* Active tool indicator */}
          {activeTool !== "select" && <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-[#222]/90 border border-[#444] rounded px-3 py-1 text-[10px] text-primary capitalize">{activeTool.replace("_", " ").replace("measure ", "")}</div>}

          {/* Selected token panel */}
          {selectedToken && activeSession && (
            <div className="absolute bottom-12 left-14 z-20 bg-[#222]/95 backdrop-blur-sm border border-[#444] rounded-lg p-3 w-56 shadow-xl" data-testid="selected-token-panel">
              <div className="flex items-center justify-between mb-2"><h4 className="text-xs font-medium text-white truncate">{selectedToken.name}</h4><div className="flex gap-0.5"><button className="p-1 text-[#888] hover:text-red-400" onClick={() => removeToken(selectedToken.id)} data-testid="remove-token"><Trash2 className="w-3 h-3" /></button><button className="p-1 text-[#888] hover:text-white" onClick={() => setSelectedToken(null)}><X className="w-3 h-3" /></button></div></div>
              <div className="flex gap-3 mb-2 text-[10px]"><span className="text-[#888]"><Heart className="w-3 h-3 inline text-green-400" /> {selectedToken.hp}/{selectedToken.max_hp}</span><span className="text-[#888]"><Shield className="w-3 h-3 inline text-blue-400" /> {selectedToken.ac}</span></div>
              <div className="h-1.5 bg-[#333] rounded-full overflow-hidden mb-2"><div className="h-full rounded-full transition-all" style={{ width: `${selectedToken.max_hp > 0 ? (selectedToken.hp / selectedToken.max_hp) * 100 : 0}%`, backgroundColor: (selectedToken.hp / (selectedToken.max_hp || 1)) > 0.5 ? "#22c55e" : (selectedToken.hp / (selectedToken.max_hp || 1)) > 0.25 ? "#eab308" : "#ef4444" }} /></div>
              <div className="flex gap-1"><Input value={hpInput} onChange={e => setHpInput(e.target.value)} placeholder="HP" className="h-6 text-[10px] bg-[#333] border-[#555] rounded-sm flex-1 text-white" data-testid="hp-amount-input" /><Button size="sm" className="h-6 rounded-sm text-[9px] bg-red-600/80 hover:bg-red-600 text-white px-2" onClick={() => { const v = parseInt(hpInput); if (v) { updateTokenHp(selectedToken.id, -v); setHpInput(""); } }} data-testid="damage-token">Dmg</Button><Button size="sm" className="h-6 rounded-sm text-[9px] bg-green-600/80 hover:bg-green-600 text-white px-2" onClick={() => { const v = parseInt(hpInput); if (v) { updateTokenHp(selectedToken.id, v); setHpInput(""); } }} data-testid="heal-token">Heal</Button></div>
              {/* Per-token Vision (feet) — used by dynamic lighting when Vision
                  Mode is Token Vision. Bright fully illuminates; Dim gives
                  half-light; Dark (darkvision) reveals as dim in darkness. */}
              <div className="mt-2 pt-2 border-t border-[#444]" data-testid="token-vision-controls">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-[#888] uppercase tracking-wider flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Vision (ft)
                  </span>
                  <div className="flex gap-0.5">
                    <button
                      className="text-[9px] text-[#666] hover:text-primary px-1.5 py-0.5 rounded-sm border border-[#444]"
                      onClick={() => updateTokenVision(selectedToken.id, { vision_bright: 0, vision_dim: 0, vision_dark: 60 })}
                      title="Darkvision 60 ft (typical race)"
                      data-testid="vision-preset-darkvision"
                    >Dark 60</button>
                    <button
                      className="text-[9px] text-[#666] hover:text-primary px-1.5 py-0.5 rounded-sm border border-[#444]"
                      onClick={() => updateTokenVision(selectedToken.id, { vision_bright: 20, vision_dim: 20, vision_dark: 0 })}
                      title="Torchlight (20 ft bright + 20 ft dim)"
                      data-testid="vision-preset-torch"
                    >Torch</button>
                    <button
                      className="text-[9px] text-[#666] hover:text-red-400 px-1.5 py-0.5 rounded-sm border border-[#444]"
                      onClick={() => updateTokenVision(selectedToken.id, { vision_bright: 0, vision_dim: 0, vision_dark: 0 })}
                      title="No vision (token doesn't reveal)"
                      data-testid="vision-preset-off"
                    >Off</button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { key: "vision_bright", label: "Bright", accent: "border-yellow-500/40" },
                    { key: "vision_dim",    label: "Dim",    accent: "border-blue-500/40" },
                    { key: "vision_dark",   label: "Dark",   accent: "border-purple-500/40" },
                  ].map(({ key, label, accent }) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-[8px] text-[#666] uppercase text-center mb-0.5">{label}</span>
                      <Input
                        type="number"
                        min={0}
                        max={999}
                        step={5}
                        value={selectedToken[key] ?? 0}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                          // Optimistic local update so the input stays snappy
                          setSelectedToken(p => p?.id === selectedToken.id ? { ...p, [key]: v } : p);
                        }}
                        onBlur={(e) => {
                          const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                          updateTokenVision(selectedToken.id, { [key]: v });
                        }}
                        className={`h-6 text-[10px] text-center bg-[#333] border ${accent} rounded-sm text-white p-0`}
                        data-testid={`token-${key.replace("vision_", "vision-")}-input`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {selectedToken.source_type === "character" && charEquipment.length > 0 && <div className="mt-2 pt-2 border-t border-[#444]"><p className="text-[9px] text-[#888] uppercase mb-1">Equipment</p>{charEquipment.map(item => <div key={item.id} className="flex items-center gap-1 text-[10px]"><button onClick={() => toggleEquip(item.id)} className={`w-3 h-3 rounded border flex-shrink-0 ${item.equipped ? "bg-primary border-primary" : "border-[#555]"}`} /><span className={item.equipped ? "text-primary" : "text-white"}>{item.name}</span></div>)}</div>}
            </div>
          )}

          {/* Selected map asset toolbar (size / rotate / delete) */}
          {selectedAssetId && (() => {
            const a = mapAssets.find(x => x.id === selectedAssetId);
            if (!a) return null;
            const def = findAsset(a.asset_key);
            const sz = Math.round(a.size || 40);
            const rot = Math.round(((a.rotation || 0) % 360 + 360) % 360);
            const setSize = (next) => setMapAssets(p => p.map(x => x.id === a.id ? { ...x, size: Math.max(16, Math.min(600, next)) } : x));
            const setRot  = (next) => setMapAssets(p => p.map(x => x.id === a.id ? { ...x, rotation: next } : x));
            return (
              <div className="absolute bottom-12 left-14 z-20 bg-[#222]/95 backdrop-blur-sm border border-amber-500/50 rounded-lg p-3 w-64 shadow-xl" data-testid="selected-asset-panel">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-amber-300 truncate flex items-center gap-1.5">
                    <img src={def?.url} alt="" className="w-4 h-4 object-contain" />
                    {def?.name || "Asset"}
                  </h4>
                  <div className="flex gap-0.5">
                    <button className="p-1 text-[#888] hover:text-red-400" onClick={() => { removeMapAsset(a.id); setSelectedAssetId(null); }} data-testid="remove-asset" title="Delete (Del)"><Trash2 className="w-3 h-3" /></button>
                    <button className="p-1 text-[#888] hover:text-white" onClick={() => setSelectedAssetId(null)} title="Deselect (Esc)"><X className="w-3 h-3" /></button>
                  </div>
                </div>
                {/* Size */}
                <div className="mb-2">
                  <div className="flex items-center justify-between text-[10px] text-[#888] mb-1">
                    <span className="flex items-center gap-1"><Maximize2 className="w-3 h-3" /> Size</span>
                    <span className="text-amber-300 font-mono" data-testid="asset-size-display">{sz}px</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setSize(sz / 1.15)} className="h-7 w-7 rounded-sm bg-[#333] hover:bg-[#444] text-white text-[14px] leading-none border border-[#555]" data-testid="asset-size-down" title="Smaller">−</button>
                    <input
                      type="range" min={16} max={600} step={1} value={sz}
                      onChange={e => setSize(parseInt(e.target.value))}
                      className="flex-1 accent-amber-500"
                      data-testid="asset-size-slider"
                    />
                    <button onClick={() => setSize(sz * 1.15)} className="h-7 w-7 rounded-sm bg-[#333] hover:bg-[#444] text-white text-[14px] leading-none border border-[#555]" data-testid="asset-size-up" title="Larger">+</button>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {[40, 80, 120, 200, 320].map(preset => (
                      <button key={preset} onClick={() => setSize(preset)} className={`flex-1 text-[9px] py-0.5 rounded-sm border ${sz === preset ? "bg-amber-500/20 border-amber-500/60 text-amber-300" : "border-[#444] text-[#888] hover:text-white"}`} data-testid={`asset-size-preset-${preset}`}>{preset}</button>
                    ))}
                  </div>
                </div>
                {/* Rotation */}
                <div className="mb-1">
                  <div className="flex items-center justify-between text-[10px] text-[#888] mb-1">
                    <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Rotation</span>
                    <span className="text-amber-300 font-mono" data-testid="asset-rot-display">{rot}°</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setRot((a.rotation || 0) - 15)} className="h-7 w-8 rounded-sm bg-[#333] hover:bg-[#444] text-white text-[10px] border border-[#555]" data-testid="asset-rot-down" title="Rotate -15°">−15°</button>
                    <button onClick={() => setRot(0)} className="h-7 flex-1 rounded-sm bg-[#333] hover:bg-[#444] text-[#aaa] hover:text-white text-[10px] border border-[#555]" data-testid="asset-rot-reset" title="Reset rotation">Reset</button>
                    <button onClick={() => setRot((a.rotation || 0) + 15)} className="h-7 w-8 rounded-sm bg-[#333] hover:bg-[#444] text-white text-[10px] border border-[#555]" data-testid="asset-rot-up" title="Rotate +15°">+15°</button>
                  </div>
                </div>
                <p className="text-[9px] text-[#666] leading-snug mt-1">Shift+scroll = resize · Scroll = rotate · Drag to move · Del to remove</p>
              </div>
            );
          })()}
        </div>

        {/* RIGHT INFO PANEL */}
        {infoPanel && (
          <div className="w-72 bg-[#1e1e1e] border-l border-[#333] flex flex-col flex-shrink-0 overflow-hidden z-20" data-testid="vtt-info-panel">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#333]"><h3 className="text-xs font-medium text-white capitalize">{infoPanel}</h3><button onClick={() => setInfoPanel(null)} className="text-[#888] hover:text-white"><X className="w-4 h-4" /></button></div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {infoPanel === "characters" && allCharacters.map(ch => <div key={ch.id} className="p-2 rounded-sm bg-[#2a2a2a] border border-[#444]"><p className="text-xs font-medium text-white">{ch.name}</p><p className="text-[10px] text-[#888]">{ch.race} {ch.character_class} Lvl {ch.level}</p><div className="flex gap-2 mt-1 text-[10px]">{ch.armor_class && <span className="text-[#888]"><Shield className="w-2.5 h-2.5 inline" /> {ch.armor_class}</span>}{ch.hit_points && <span className="text-[#888]"><Heart className="w-2.5 h-2.5 inline" /> {ch.hit_points}</span>}</div></div>)}
              {infoPanel === "characters" && allCharacters.length === 0 && <p className="text-[10px] text-[#666] italic text-center py-4">No characters</p>}
              {infoPanel === "npcs" && allNpcs.map(npc => <div key={npc.id} className="p-2 rounded-sm bg-[#2a2a2a] border border-[#444]"><p className="text-xs font-medium text-white">{npc.name}</p><p className="text-[10px] text-[#888]">{npc.profession || npc.race || "NPC"}</p></div>)}
              {infoPanel === "npcs" && allNpcs.length === 0 && <p className="text-[10px] text-[#666] italic text-center py-4">No NPCs</p>}
              {infoPanel === "items" && allItems.map(item => <div key={item.id} className="p-2 rounded-sm bg-[#2a2a2a] border border-[#444]"><p className="text-xs font-medium text-white">{item.name}</p><p className="text-[10px] text-[#888] capitalize">{item.rarity} {item.item_type}</p></div>)}
              {infoPanel === "items" && allItems.length === 0 && <p className="text-[10px] text-[#666] italic text-center py-4">No items</p>}
              {infoPanel === "handouts" && allHandouts.map(h => (
                <div key={h.id} className="p-2 rounded-sm bg-[#2a2a2a] border border-[#444]">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-white truncate">{h.name}</p>
                    <Button size="sm" variant="ghost" className="h-5 text-[9px] text-primary hover:text-white px-1.5"
                      onClick={() => { emitShareHandout({ name: h.name, content: h.content, image_url: h.image_url }); setSharedHandout({ name: h.name, content: h.content, image_url: h.image_url }); toast.success(`Shared "${h.name}" with players`); }}
                      data-testid={`share-handout-vtt-${h.id}`}
                    >Share</Button>
                  </div>
                  <p className="text-[10px] text-[#888] line-clamp-2 mt-0.5">{h.content?.substring(0, 60) || "Empty"}</p>
                </div>
              ))}
              {infoPanel === "handouts" && allHandouts.length === 0 && <p className="text-[10px] text-[#666] italic text-center py-4">No handouts</p>}
            </div>
          </div>
        )}

        {/* TOKEN STAT BLOCK PANEL (double-click) */}
        <VTTTokenStatPanel
          token={tokenInfoOpen}
          fullStatBlock={fullStatBlock}
          setFullStatBlock={setFullStatBlock}
          conditionPickerOpen={conditionPickerOpen}
          setConditionPickerOpen={setConditionPickerOpen}
          hpInput={hpInput}
          setHpInput={setHpInput}
          charEquipment={charEquipment}
          onClose={() => { setTokenInfoOpen(null); setFullStatBlock(null); setConditionPickerOpen(false); }}
          onUpdateSize={updateTokenSize}
          onUpdateHp={updateTokenHp}
          onSetTokenInfoOpen={setTokenInfoOpen}
          onToggleCondition={toggleCondition}
          onAddToInitiative={addToInitiative}
          onRollDice={rollDice}
          onToggleEquip={toggleEquip}
          onToggleDead={toggleDead}
          onRemove={removeToken}
          api={API}
          sessionName={activeSession?.name}
        />
      </div>

      {/* COMBAT TRACKER POPUP */}
      <VTTCombatTracker
        show={showInitTracker}
        initiativeOrder={initiativeOrder}
        activeSession={activeSession}
        combatActive={combatActive}
        selectedToken={selectedToken}
        setInitiativeOrder={setInitiativeOrder}
        setActiveInitiative={setActiveInitiative}
        setSelectedToken={setSelectedToken}
        addToInitiative={addToInitiative}
        removeFromInitiative={removeFromInitiative}
        onClose={() => { setShowInitTracker(false); setCombatActive(false); }}
        onEndCombat={() => { setCombatActive(false); setInitiativeOrder([]); setShowInitTracker(false); pushCombatLog("turn", "Combat ended"); toast.info("Combat ended"); }}
        combatLog={combatLog}
        pushCombatLog={pushCombatLog}
        clearCombatLog={clearCombatLog}
      />

      {/* CAMPAIGN TOOLS PANEL (Story, Sessions, Lore, etc.) */}
      {activeSession && <VTTCampaignPanel campaignId={campaignId} />}

      {/* SHARED HANDOUT POPUP */}
      {sharedHandout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSharedHandout(null)}>
          <div className="bg-[#1e1e1e] border border-[#444] rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="shared-handout-popup">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#444] bg-primary/10">
              <h3 className="text-base font-serif text-white">{sharedHandout.name}</h3>
              <button onClick={() => setSharedHandout(null)} className="text-[#888] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {sharedHandout.image_url && (
              <div className="h-48 overflow-hidden"><img src={sharedHandout.image_url} alt="" className="w-full h-full object-cover" /></div>
            )}
            <div className="p-4 max-h-[40vh] overflow-y-auto">
              <p className="text-sm text-[#ccc] whitespace-pre-wrap leading-relaxed">{sharedHandout.content || "No content"}</p>
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM BAR */}
      {activeSession && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-[#222] border-t border-[#333] flex-shrink-0 z-30" data-testid="vtt-bottom-bar">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#888]">{(activeSession.tokens || []).length} tokens</span>
            {selectedTokenIds.length > 1 && <span className="text-[10px] text-primary font-medium">{selectedTokenIds.length} selected</span>}
            {initiativeOrder.length > 0 && (
              <Button size="sm" variant={showInitTracker ? "default" : "ghost"} className="h-7 text-[10px] text-[#888] hover:text-white" onClick={() => setShowInitTracker(!showInitTracker)} data-testid="toggle-init-tracker">
                <Swords className="w-3 h-3 mr-1" /> Initiative ({initiativeOrder.length})
              </Button>
            )}
          </div>
          <Button size="sm" className={`h-7 ${sessionActive ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"} text-white rounded-sm text-xs px-4`} onClick={startSession} data-testid="start-session-btn"><Swords className="w-3 h-3 mr-1" /> {sessionActive ? "END SESSION" : "START SESSION"}</Button>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={zoomOut}><ZoomOut className="w-3.5 h-3.5" /></Button>
            <span className="text-[10px] text-[#888] w-10 text-center">{Math.round(viewport.scale * 100)}%</span>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={zoomIn}><ZoomIn className="w-3.5 h-3.5" /></Button>
            <div className="w-px h-4 bg-[#444] mx-1" />
            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={fitToTokens} data-testid="fit-tokens"><Maximize2 className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-[#888]" onClick={resetView} data-testid="reset-view"><RotateCcw className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="ghost" className={`h-7 text-[10px] gap-1 ${activeSession.grid_enabled ? "text-primary" : "text-[#888]"}`} onClick={toggleGrid} data-testid="toggle-grid"><Grid3X3 className="w-3.5 h-3.5" />{activeSession.grid_enabled ? "Grid" : "No Grid"}</Button>
            <Button size="sm" variant="ghost" className={`h-7 text-[10px] ${snapToGrid ? "text-primary" : "text-[#888]"}`} onClick={() => setSnapToGrid(!snapToGrid)} data-testid="toggle-snap" title="Snap to grid">{snapToGrid ? "Snap ON" : "Snap OFF"}</Button>
          </div>
        </div>
      )}

      {/* CREATE SESSION DIALOG */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#222] border-[#444] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl text-white">New VTT Session</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><label className="text-sm font-medium text-white block mb-1">Session Name</label><Input value={newSessionName} onChange={e => setNewSessionName(e.target.value)} placeholder="Combat Encounter" className="bg-[#333] border-[#555] rounded-sm text-white" data-testid="vtt-session-name-input" /></div>
            <div><label className="text-sm font-medium text-white block mb-1">Map</label><Select value={selectedMapId} onValueChange={setSelectedMapId}><SelectTrigger className="bg-[#333] border-[#555] rounded-sm text-white" data-testid="vtt-map-select"><SelectValue placeholder="Select a map or drag-drop later" /></SelectTrigger><SelectContent className="bg-[#333] border-[#555]"><SelectItem value="none">No map (drag & drop later)</SelectItem>{maps.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={createSession} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="create-vtt-session-submit">Create Session</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MAP PICKER DIALOG */}
      <Dialog open={mapPickerOpen} onOpenChange={setMapPickerOpen}>
        <DialogContent className="bg-[#222] border-[#444] max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle className="font-serif text-xl text-white">Select Map</DialogTitle></DialogHeader>
          <p className="text-xs text-[#888] mb-3">Click a map to apply it to the current session, or drag & drop an image onto the VTT canvas.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 overflow-y-auto max-h-[50vh]">
            {maps.length === 0 ? <p className="text-sm text-[#666] col-span-3 text-center py-8">No maps uploaded yet. Go to Maps page to upload, or drag & drop an image onto the VTT canvas.</p> : maps.map(m => {
              const fullUrl = resolveImageUrl(m.storage_path || m.image_path || m.image_url);
              return (
                <button key={m.id} className="group rounded-lg overflow-hidden border border-[#444] hover:border-primary transition-colors bg-[#2a2a2a] text-left" onClick={() => applyMapToSession(m)} data-testid={`map-pick-${m.id}`}>
                  {fullUrl ? (
                    <img src={fullUrl} alt={m.name} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 bg-[#333] flex items-center justify-center"><Map className="w-8 h-8 text-[#555]" /></div>
                  )}
                  <div className="p-2">
                    <p className="text-xs text-white truncate group-hover:text-primary transition-colors">{m.name}</p>
                    <p className="text-[9px] text-[#888] truncate">{m.description || "No description"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VTTPage;
